import React from "react";
import { cn } from "../lib/utils";
import { motion, AnimatePresence } from "motion/react";
import { 
  Flame, 
  Zap, 
  Trees, 
  Building, 
  Landmark, 
  Cpu, 
  FlaskConical, 
  Globe,
  Pickaxe,
  Truck,
  Sprout,
  Factory,
  Car,
  ShoppingBag,
  Package,
  Pill
} from "lucide-react";

interface CompanyLogoProps {
  companyName: string;
  logoUrl?: string;
  domain?: string;
  className?: string;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  sector?: string;
}

const CUSTOM_COMPANY_LOGOS: Record<string, string> = {
  "evercove": "https://www.evercove.com/logo.png",
  "evercove.com": "https://www.evercove.com/logo.png",
  "www.evercove.com": "https://www.evercove.com/logo.png"
};

const CUSTOM_INLINE_LOGOS: Record<string, React.ReactNode> = {
  "s.pellegrino": (
    <svg viewBox="0 0 240 240" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2 bg-[#002F6C]">
      <circle cx="120" cy="120" r="105" stroke="#FFFFFF" strokeWidth="3" fill="none" />
      <path d="M120 55 L129 82 L157 82 L134 99 L143 126 L120 109 L97 126 L106 99 L83 82 L111 82 Z" fill="#E31B23" />
      <text x="120" y="165" fill="#FFFFFF" fontSize="18" fontWeight="900" fontFamily="Georgia, serif" textAnchor="middle" letterSpacing="1">S.PELLEGRINO</text>
      <text x="120" y="188" fill="#93C5FD" fontSize="10" fontWeight="bold" fontFamily="system-ui, sans-serif" textAnchor="middle" letterSpacing="2">ACQUA MINERALE</text>
    </svg>
  ),
  "san pellegrino": (
    <svg viewBox="0 0 240 240" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2 bg-[#002F6C]">
      <circle cx="120" cy="120" r="105" stroke="#FFFFFF" strokeWidth="3" fill="none" />
      <path d="M120 55 L129 82 L157 82 L134 99 L143 126 L120 109 L97 126 L106 99 L83 82 L111 82 Z" fill="#E31B23" />
      <text x="120" y="165" fill="#FFFFFF" fontSize="18" fontWeight="900" fontFamily="Georgia, serif" textAnchor="middle" letterSpacing="1">S.PELLEGRINO</text>
      <text x="120" y="188" fill="#93C5FD" fontSize="10" fontWeight="bold" fontFamily="system-ui, sans-serif" textAnchor="middle" letterSpacing="2">ACQUA MINERALE</text>
    </svg>
  ),
  "san pellegrino water": (
    <svg viewBox="0 0 240 240" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2 bg-[#002F6C]">
      <circle cx="120" cy="120" r="105" stroke="#FFFFFF" strokeWidth="3" fill="none" />
      <path d="M120 55 L129 82 L157 82 L134 99 L143 126 L120 109 L97 126 L106 99 L83 82 L111 82 Z" fill="#E31B23" />
      <text x="120" y="165" fill="#FFFFFF" fontSize="18" fontWeight="900" fontFamily="Georgia, serif" textAnchor="middle" letterSpacing="1">S.PELLEGRINO</text>
      <text x="120" y="188" fill="#93C5FD" fontSize="10" fontWeight="bold" fontFamily="system-ui, sans-serif" textAnchor="middle" letterSpacing="2">ACQUA MINERALE</text>
    </svg>
  ),
  "sanpellegrino.com": (
    <svg viewBox="0 0 240 240" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2 bg-[#002F6C]">
      <circle cx="120" cy="120" r="105" stroke="#FFFFFF" strokeWidth="3" fill="none" />
      <path d="M120 55 L129 82 L157 82 L134 99 L143 126 L120 109 L97 126 L106 99 L83 82 L111 82 Z" fill="#E31B23" />
      <text x="120" y="165" fill="#FFFFFF" fontSize="18" fontWeight="900" fontFamily="Georgia, serif" textAnchor="middle" letterSpacing="1">S.PELLEGRINO</text>
      <text x="120" y="188" fill="#93C5FD" fontSize="10" fontWeight="bold" fontFamily="system-ui, sans-serif" textAnchor="middle" letterSpacing="2">ACQUA MINERALE</text>
    </svg>
  ),
  "cartier": (
    <svg viewBox="0 0 240 240" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-3 bg-[#1C1917]">
      <circle cx="120" cy="120" r="105" stroke="#D97706" strokeWidth="2" fill="none" />
      <circle cx="120" cy="120" r="92" stroke="#D97706" strokeWidth="1" strokeDasharray="3 3" fill="none" />
      <text x="120" y="125" fill="#FBBF24" fontSize="30" fontWeight="300" fontFamily="Georgia, serif" fontStyle="italic" textAnchor="middle" letterSpacing="0.5">Cartier</text>
      <text x="120" y="165" fill="#F5F5F4" fontSize="9" fontWeight="bold" fontFamily="system-ui, sans-serif" textAnchor="middle" letterSpacing="5">PARIS</text>
    </svg>
  ),
  "cartier watches & jewelry": (
    <svg viewBox="0 0 240 240" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-3 bg-[#1C1917]">
      <circle cx="120" cy="120" r="105" stroke="#D97706" strokeWidth="2" fill="none" />
      <circle cx="120" cy="120" r="92" stroke="#D97706" strokeWidth="1" strokeDasharray="3 3" fill="none" />
      <text x="120" y="125" fill="#FBBF24" fontSize="30" fontWeight="300" fontFamily="Georgia, serif" fontStyle="italic" textAnchor="middle" letterSpacing="0.5">Cartier</text>
      <text x="120" y="165" fill="#F5F5F4" fontSize="9" fontWeight="bold" fontFamily="system-ui, sans-serif" textAnchor="middle" letterSpacing="5">PARIS</text>
    </svg>
  ),
  "cartier.com": (
    <svg viewBox="0 0 240 240" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-3 bg-[#1C1917]">
      <circle cx="120" cy="120" r="105" stroke="#D97706" strokeWidth="2" fill="none" />
      <circle cx="120" cy="120" r="92" stroke="#D97706" strokeWidth="1" strokeDasharray="3 3" fill="none" />
      <text x="120" y="125" fill="#FBBF24" fontSize="30" fontWeight="300" fontFamily="Georgia, serif" fontStyle="italic" textAnchor="middle" letterSpacing="0.5">Cartier</text>
      <text x="120" y="165" fill="#F5F5F4" fontSize="9" fontWeight="bold" fontFamily="system-ui, sans-serif" textAnchor="middle" letterSpacing="5">PARIS</text>
    </svg>
  ),
  "evercove": (
    <img 
      src="/evercove.png" 
      alt="Evercove Logo" 
      className="w-full h-full object-contain p-1 animate-fade-in" 
      referrerPolicy="no-referrer" 
    />
  ),
  "evercove.com": (
    <img 
      src="/evercove.png" 
      alt="Evercove Logo" 
      className="w-full h-full object-contain p-1 animate-fade-in" 
      referrerPolicy="no-referrer" 
    />
  ),
  "conocophillips": (
    <svg viewBox="0 0 240 240" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2 bg-[#E31B23]">
      <path d="M50 70 L190 70 L120 180 Z" fill="#FFFFFF" />
      <circle cx="120" cy="110" r="28" fill="#E31B23" />
      <text x="120" y="215" fill="#FFFFFF" fontSize="16" fontWeight="bold" textAnchor="middle" letterSpacing="1">CONOCO</text>
    </svg>
  ),
  "conocophillips.com": (
    <svg viewBox="0 0 240 240" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2 bg-[#E31B23]">
      <path d="M50 70 L190 70 L120 180 Z" fill="#FFFFFF" />
      <circle cx="120" cy="110" r="28" fill="#E31B23" />
      <text x="120" y="215" fill="#FFFFFF" fontSize="16" fontWeight="bold" textAnchor="middle" letterSpacing="1">CONOCO</text>
    </svg>
  ),
  "dow": (
    <svg viewBox="0 0 240 240" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect x="25" y="25" width="190" height="190" rx="40" fill="#E31B23" />
      <text x="120" y="145" fill="#FFFFFF" fontSize="64" fontWeight="900" fontFamily="sans-serif" textAnchor="middle">DOW</text>
    </svg>
  ),
  "dow chemical": (
    <svg viewBox="0 0 240 240" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect x="25" y="25" width="190" height="190" rx="40" fill="#E31B23" />
      <text x="120" y="145" fill="#FFFFFF" fontSize="64" fontWeight="900" fontFamily="sans-serif" textAnchor="middle">DOW</text>
    </svg>
  ),
  "dow.com": (
    <svg viewBox="0 0 240 240" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect x="25" y="25" width="190" height="190" rx="40" fill="#E31B23" />
      <text x="120" y="145" fill="#FFFFFF" fontSize="64" fontWeight="900" fontFamily="sans-serif" textAnchor="middle">DOW</text>
    </svg>
  ),
  "indofood": (
    <svg viewBox="0 0 240 240" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-1 bg-[#0A3982]">
      <path d="M 30 180 Q 120 120 210 180 L 210 220 L 30 220 Z" fill="#F8B114" opacity="0.6"/>
      <path d="M 30 190 Q 120 140 210 190 L 210 220 L 30 220 Z" fill="#E31B23" />
      <text x="120" y="120" fill="#FFFFFF" fontSize="36" fontWeight="900" textAnchor="middle">Indofood</text>
    </svg>
  ),
  "indofood.com": (
    <svg viewBox="0 0 240 240" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-1 bg-[#0A3982]">
      <path d="M 30 180 Q 120 120 210 180 L 210 220 L 30 220 Z" fill="#F8B114" opacity="0.6"/>
      <path d="M 30 190 Q 120 140 210 190 L 210 220 L 30 220 Z" fill="#E31B23" />
      <text x="120" y="120" fill="#FFFFFF" fontSize="36" fontWeight="900" textAnchor="middle">Indofood</text>
    </svg>
  ),
  "fortescue": (
    <svg viewBox="0 0 240 240" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2 bg-[#022A4E]">
      <circle cx="120" cy="110" r="50" stroke="#FFC72C" strokeWidth="12" strokeLinecap="round" strokeDasharray="240 60" />
      <text x="120" y="205" fill="#FFFFFF" fontSize="24" fontWeight="800" textAnchor="middle" letterSpacing="0.5">FORTESCUE</text>
    </svg>
  ),
  "fortescue metals": (
    <svg viewBox="0 0 240 240" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2 bg-[#022A4E]">
      <circle cx="120" cy="110" r="50" stroke="#FFC72C" strokeWidth="12" strokeLinecap="round" strokeDasharray="240 60" />
      <text x="120" y="205" fill="#FFFFFF" fontSize="24" fontWeight="800" textAnchor="middle" letterSpacing="0.5">FORTESCUE</text>
    </svg>
  ),
  "fortescuemetals.com": (
    <svg viewBox="0 0 240 240" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2 bg-[#022A4E]">
      <circle cx="120" cy="110" r="50" stroke="#FFC72C" strokeWidth="12" strokeLinecap="round" strokeDasharray="240 60" />
      <text x="120" y="205" fill="#FFFFFF" fontSize="24" fontWeight="800" textAnchor="middle" letterSpacing="0.5">FORTESCUE</text>
    </svg>
  ),
  "fortescue.com": (
    <svg viewBox="0 0 240 240" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2 bg-[#022A4E]">
      <circle cx="120" cy="110" r="50" stroke="#FFC72C" strokeWidth="12" strokeLinecap="round" strokeDasharray="240 60" />
      <text x="120" y="205" fill="#FFFFFF" fontSize="24" fontWeight="800" textAnchor="middle" letterSpacing="0.5">FORTESCUE</text>
    </svg>
  ),
  "gazprom": (
    <svg viewBox="0 0 240 240" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-1.5">
      <path
        d="M 152 45 C 131 69, 137 101, 153 113 C 170 125, 189 123, 196 102 C 204 79, 184 57, 184 45 C 182 56, 172 68, 169 57 C 166 45, 152 45, 152 45 Z"
        fill="#00B0FF"
      />
      <path
        d="M 129 113 L 202 113 A 88 88 0 1 1 120 32 L 120 73 A 47 47 0 1 0 129 113"
        fill="#00529B"
      />
    </svg>
  ),
  "gazprom.com": (
    <svg viewBox="0 0 240 240" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-1.5">
      <path
        d="M 152 45 C 131 69, 137 101, 153 113 C 170 125, 189 123, 196 102 C 204 79, 184 57, 184 45 C 182 56, 172 68, 169 57 C 166 45, 152 45, 152 45 Z"
        fill="#00B0FF"
      />
      <path
        d="M 129 113 L 202 113 A 88 88 0 1 1 120 32 L 120 73 A 47 47 0 1 0 129 113"
        fill="#00529B"
      />
    </svg>
  ),
  "gazprom.ru": (
    <svg viewBox="0 0 240 240" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-1.5">
      <path
        d="M 152 45 C 131 69, 137 101, 153 113 C 170 125, 189 123, 196 102 C 204 79, 184 57, 184 45 C 182 56, 172 68, 169 57 C 166 45, 152 45, 152 45 Z"
        fill="#00B0FF"
      />
      <path
        d="M 129 113 L 202 113 A 88 88 0 1 1 120 32 L 120 73 A 47 47 0 1 0 129 113"
        fill="#00529B"
      />
    </svg>
  ),
  "saudi aramco": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <circle cx="100" cy="100" r="90" fill="none" stroke="#2E7D32" strokeWidth="5" />
      <g transform="translate(100, 100)">
        {Array.from({ length: 16 }).map((_, i) => (
          <path
            key={i}
            d="M 0 -8 C 12 -35, 0 -55, 0 -60 C 0 -55, -12 -35, 0 -8 Z"
            fill="#00ACC1"
            transform={`rotate(${i * 22.5})`}
            opacity={0.9}
          />
        ))}
        <circle cx="0" cy="0" r="24" fill="#2E7D32" />
        <circle cx="0" cy="0" r="10" fill="#E8F5E9" />
      </g>
    </svg>
  ),
  "aramco.com": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <circle cx="100" cy="100" r="90" fill="none" stroke="#2E7D32" strokeWidth="5" />
      <g transform="translate(100, 100)">
        {Array.from({ length: 16 }).map((_, i) => (
          <path
            key={i}
            d="M 0 -8 C 12 -35, 0 -55, 0 -60 C 0 -55, -12 -35, 0 -8 Z"
            fill="#00ACC1"
            transform={`rotate(${i * 22.5})`}
            opacity={0.9}
          />
        ))}
        <circle cx="0" cy="0" r="24" fill="#2E7D32" />
        <circle cx="0" cy="0" r="10" fill="#E8F5E9" />
      </g>
    </svg>
  ),
  "coal india": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="12" fill="#E65100" />
      <path d="M 40 145 L 80 55 L 120 145 Z" fill="#212121" />
      <path d="M 80 145 L 120 55 L 160 145 Z" fill="#37474F" />
      <circle cx="100" cy="70" r="15" fill="#FFB74D" />
      <text x="100" y="175" fill="#212121" fontSize="22" fontWeight="900" fontFamily="sans-serif" textAnchor="middle" letterSpacing="1">COAL</text>
    </svg>
  ),
  "coalindia.in": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="12" fill="#E65100" />
      <path d="M 40 145 L 80 55 L 120 145 Z" fill="#212121" />
      <path d="M 80 145 L 120 55 L 160 145 Z" fill="#37474F" />
      <circle cx="100" cy="70" r="15" fill="#FFB74D" />
      <text x="100" y="175" fill="#212121" fontSize="22" fontWeight="900" fontFamily="sans-serif" textAnchor="middle" letterSpacing="1">COAL</text>
    </svg>
  ),
  "rosneft": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="12" fill="#212121" />
      <path
        d="M 100 25 L 132 80 L 195 80 L 145 115 L 164 175 L 100 138 L 36 175 L 55 115 L 5 80 L 68 80 Z"
        fill="#FFD54F"
        stroke="#F57F17"
        strokeWidth="3"
        strokeLinejoin="round"
      />
      <circle cx="100" cy="110" r="18" fill="#FFFFFF" />
    </svg>
  ),
  "rosneft.com": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="12" fill="#212121" />
      <path
        d="M 100 25 L 132 80 L 195 80 L 145 115 L 164 175 L 100 138 L 36 175 L 55 115 L 5 80 L 68 80 Z"
        fill="#FFD54F"
        stroke="#F57F17"
        strokeWidth="3"
        strokeLinejoin="round"
      />
      <circle cx="100" cy="110" r="18" fill="#FFFFFF" />
    </svg>
  ),
  "rosneft.ru": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="12" fill="#212121" />
      <path
        d="M 100 25 L 132 80 L 195 80 L 145 115 L 164 175 L 100 138 L 36 175 L 55 115 L 5 80 L 68 80 Z"
        fill="#FFD54F"
        stroke="#F57F17"
        strokeWidth="3"
        strokeLinejoin="round"
      />
      <circle cx="100" cy="110" r="18" fill="#FFFFFF" />
    </svg>
  ),
  "lukoil": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2.5">
      <rect width="200" height="200" rx="12" fill="#C62828" />
      <path d="M 45 140 L 45 60 L 70 60 L 70 115 L 110 115 L 110 140 Z" fill="#FFFFFF" />
      <circle cx="145" cy="85" r="18" fill="#FFFFFF" />
      <path d="M 145 103 L 145 140" stroke="#FFFFFF" strokeWidth="20" strokeLinecap="round" />
    </svg>
  ),
  "lukoil.com": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2.5">
      <rect width="200" height="200" rx="12" fill="#C62828" />
      <path d="M 45 140 L 45 60 L 70 60 L 70 115 L 110 115 L 110 140 Z" fill="#FFFFFF" />
      <circle cx="145" cy="85" r="18" fill="#FFFFFF" />
      <path d="M 145 103 L 145 140" stroke="#FFFFFF" strokeWidth="20" strokeLinecap="round" />
    </svg>
  ),
  "lukoil.ru": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2.5">
      <rect width="200" height="200" rx="12" fill="#C62828" />
      <path d="M 45 140 L 45 60 L 70 60 L 70 115 L 110 115 L 110 140 Z" fill="#FFFFFF" />
      <circle cx="145" cy="85" r="18" fill="#FFFFFF" />
      <path d="M 145 103 L 145 140" stroke="#FFFFFF" strokeWidth="20" strokeLinecap="round" />
    </svg>
  ),
  "sinopec": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <circle cx="100" cy="100" r="90" fill="#D32F2F" />
      <circle cx="100" cy="100" r="42" fill="none" stroke="#FFFFFF" strokeWidth="16" />
      <g transform="translate(100, 100)">
        {Array.from({ length: 6 }).map((_, i) => (
          <path
            key={i}
            d="M 0 0 C 8 -22, -8 -22, 0 0 Z"
            fill="#FFFFFF"
            transform={`rotate(${i * 60})`}
          />
        ))}
      </g>
    </svg>
  ),
  "china petroleum & chemical": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <circle cx="100" cy="100" r="90" fill="#D32F2F" />
      <circle cx="100" cy="100" r="42" fill="none" stroke="#FFFFFF" strokeWidth="16" />
      <g transform="translate(100, 100)">
        {Array.from({ length: 6 }).map((_, i) => (
          <path
            key={i}
            d="M 0 0 C 8 -22, -8 -22, 0 0 Z"
            fill="#FFFFFF"
            transform={`rotate(${i * 60})`}
          />
        ))}
      </g>
    </svg>
  ),
  "sinopec.com": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <circle cx="100" cy="100" r="90" fill="#D32F2F" />
      <circle cx="100" cy="100" r="42" fill="none" stroke="#FFFFFF" strokeWidth="16" />
      <g transform="translate(100, 100)">
        {Array.from({ length: 6 }).map((_, i) => (
          <path
            key={i}
            d="M 0 0 C 8 -22, -8 -22, 0 0 Z"
            fill="#FFFFFF"
            transform={`rotate(${i * 60})`}
          />
        ))}
      </g>
    </svg>
  ),
  "petrochina": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <circle cx="100" cy="100" r="90" fill="#E53935" />
      <circle cx="100" cy="100" r="50" fill="#FFEB3B" />
      <g transform="translate(100, 100)">
        {Array.from({ length: 8 }).map((_, i) => (
          <path
            key={i}
            d="M 0 -15 C 10 -40, -10 -40, 0 -15 Z"
            fill="#E53935"
            transform={`rotate(${i * 45})`}
          />
        ))}
        <circle cx="0" cy="0" r="18" fill="#E53935" />
      </g>
    </svg>
  ),
  "petrochina.com.cn": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <circle cx="100" cy="100" r="90" fill="#E53935" />
      <circle cx="100" cy="100" r="50" fill="#FFEB3B" />
      <g transform="translate(100, 100)">
        {Array.from({ length: 8 }).map((_, i) => (
          <path
            key={i}
            d="M 0 -15 C 10 -40, -10 -40, 0 -15 Z"
            fill="#E53935"
            transform={`rotate(${i * 45})`}
          />
        ))}
        <circle cx="0" cy="0" r="18" fill="#E53935" />
      </g>
    </svg>
  ),
  "jbs": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="12" fill="#E53935" />
      <text x="100" y="125" fill="#FFFFFF" fontSize="64" fontWeight="bold" fontFamily="sans-serif" textAnchor="middle">JBS</text>
    </svg>
  ),
  "jbs.com.br": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="12" fill="#E53935" />
      <text x="100" y="125" fill="#FFFFFF" fontSize="64" fontWeight="bold" fontFamily="sans-serif" textAnchor="middle">JBS</text>
    </svg>
  ),
  // ==================== CNOOC ====================
  "cnooc": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#0B3C5D" />
      <path d="M 145 60 A 55 55 0 1 0 145 140 A 42 42 0 1 1 145 60" fill="#38BDF8" />
      <path d="M 100 68 C 112 68 122 78 122 98 C 122 115 100 138 100 138 C 100 138 78 115 78 98 C 78 78 88 68 100 68 Z" fill="#EF4444" />
      <path d="M 96 76 C 102 76 110 84 110 98" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" />
    </svg>
  ),
  "cnooc.com.cn": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#0B3C5D" />
      <path d="M 145 60 A 55 55 0 1 0 145 140 A 42 42 0 1 1 145 60" fill="#38BDF8" />
      <path d="M 100 68 C 112 68 122 78 122 98 C 122 115 100 138 100 138 C 100 138 78 115 78 98 C 78 78 88 68 100 68 Z" fill="#EF4444" />
      <path d="M 96 76 C 102 76 110 84 110 98" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" />
    </svg>
  ),
  "cnoocltd.com": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#0B3C5D" />
      <path d="M 145 60 A 55 55 0 1 0 145 140 A 42 42 0 1 1 145 60" fill="#38BDF8" />
      <path d="M 100 68 C 112 68 122 78 122 98 C 122 115 100 138 100 138 C 100 138 78 115 78 98 C 78 78 88 68 100 68 Z" fill="#EF4444" />
      <path d="M 96 76 C 102 76 110 84 110 98" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" />
    </svg>
  ),

  // ==================== NTPC ====================
  "ntpc": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#1E40AF" />
      <path d="M 45 145 C 45 95, 95 45, 145 45" stroke="#10B981" strokeWidth="15" strokeLinecap="round" />
      <path d="M 65 115 C 90 90, 110 90, 145 55" stroke="#06B6D4" strokeWidth="10" strokeLinecap="round" />
      <text x="100" y="152" fill="#FFFFFF" fontSize="38" fontWeight="900" fontFamily="sans-serif" textAnchor="middle" letterSpacing="-1">NTPC</text>
    </svg>
  ),
  "ntpc limited": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#1E40AF" />
      <path d="M 45 145 C 45 95, 95 45, 145 45" stroke="#10B981" strokeWidth="15" strokeLinecap="round" />
      <path d="M 65 115 C 90 90, 110 90, 145 55" stroke="#06B6D4" strokeWidth="10" strokeLinecap="round" />
      <text x="100" y="152" fill="#FFFFFF" fontSize="38" fontWeight="900" fontFamily="sans-serif" textAnchor="middle" letterSpacing="-1">NTPC</text>
    </svg>
  ),
  "ntpc.co.in": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#1E40AF" />
      <path d="M 45 145 C 45 95, 95 45, 145 45" stroke="#10B981" strokeWidth="15" strokeLinecap="round" />
      <path d="M 65 115 C 90 90, 110 90, 145 55" stroke="#06B6D4" strokeWidth="10" strokeLinecap="round" />
      <text x="100" y="152" fill="#FFFFFF" fontSize="38" fontWeight="900" fontFamily="sans-serif" textAnchor="middle" letterSpacing="-1">NTPC</text>
    </svg>
  ),

  // ==================== Aluminum Corp of China ====================
  "aluminum corp of china": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#1E293B" />
      <circle cx="100" cy="100" r="50" stroke="#F97316" strokeWidth="12" fill="none" />
      <path d="M 45 155 L 155 45" stroke="#94A3B8" strokeWidth="18" strokeLinecap="round" />
      <path d="M 60 140 L 140 60" stroke="#F8FAFC" strokeWidth="8" strokeLinecap="round" />
      <circle cx="100" cy="100" r="70" stroke="#38BDF8" strokeWidth="4" strokeDasharray="30 40" />
    </svg>
  ),
  "aluminum corporation of china": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#1E293B" />
      <circle cx="100" cy="100" r="50" stroke="#F97316" strokeWidth="12" fill="none" />
      <path d="M 45 155 L 155 45" stroke="#94A3B8" strokeWidth="18" strokeLinecap="round" />
      <path d="M 60 140 L 140 60" stroke="#F8FAFC" strokeWidth="8" strokeLinecap="round" />
      <circle cx="100" cy="100" r="70" stroke="#38BDF8" strokeWidth="4" strokeDasharray="30 40" />
    </svg>
  ),
  "chalco.com.cn": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#1E293B" />
      <circle cx="100" cy="100" r="50" stroke="#F97316" strokeWidth="12" fill="none" />
      <path d="M 45 155 L 155 45" stroke="#94A3B8" strokeWidth="18" strokeLinecap="round" />
      <path d="M 60 140 L 140 60" stroke="#F8FAFC" strokeWidth="8" strokeLinecap="round" />
      <circle cx="100" cy="100" r="70" stroke="#38BDF8" strokeWidth="4" strokeDasharray="30 40" />
    </svg>
  ),
  "chalco": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#1E293B" />
      <circle cx="100" cy="100" r="50" stroke="#F97316" strokeWidth="12" fill="none" />
      <path d="M 45 155 L 155 45" stroke="#94A3B8" strokeWidth="18" strokeLinecap="round" />
      <path d="M 60 140 L 140 60" stroke="#F8FAFC" strokeWidth="8" strokeLinecap="round" />
      <circle cx="100" cy="100" r="70" stroke="#38BDF8" strokeWidth="4" strokeDasharray="30 40" />
    </svg>
  ),

  // ==================== Wilmar International ====================
  "wilmar": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#166534" />
      <circle cx="100" cy="100" r="60" stroke="#EAB308" strokeWidth="6" fill="none" strokeDasharray="6 6" />
      <path d="M 100 150 C 100 115, 65 90, 55 90 C 73 102, 100 120, 100 150 Z" fill="#FACC15" />
      <path d="M 100 150 C 100 115, 135 90, 145 90 C 127 102, 100 120, 100 150 Z" fill="#FACC15" />
      <path d="M 100 150 C 100 105, 82 70, 72 70 C 86 82, 100 110, 100 150 Z" fill="#4ADE80" />
      <path d="M 100 150 C 100 105, 118 70, 128 70 C 114 82, 100 110, 100 150 Z" fill="#4ADE80" />
      <path d="M 100 150 C 100 90, 100 50, 100 50" stroke="#EAB308" strokeWidth="10" strokeLinecap="round" />
    </svg>
  ),
  "wilmar international": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#166534" />
      <circle cx="100" cy="100" r="60" stroke="#EAB308" strokeWidth="6" fill="none" strokeDasharray="6 6" />
      <path d="M 100 150 C 100 115, 65 90, 55 90 C 73 102, 100 120, 100 150 Z" fill="#FACC15" />
      <path d="M 100 150 C 100 115, 135 90, 145 90 C 127 102, 100 120, 100 150 Z" fill="#FACC15" />
      <path d="M 100 150 C 100 105, 82 70, 72 70 C 86 82, 100 110, 100 150 Z" fill="#4ADE80" />
      <path d="M 100 150 C 100 105, 118 70, 128 70 C 114 82, 100 110, 100 150 Z" fill="#4ADE80" />
      <path d="M 100 150 C 100 90, 100 50, 100 50" stroke="#EAB308" strokeWidth="10" strokeLinecap="round" />
    </svg>
  ),
  "wilmar-international.com": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#166534" />
      <circle cx="100" cy="100" r="60" stroke="#EAB308" strokeWidth="6" fill="none" strokeDasharray="6 6" />
      <path d="M 100 150 C 100 115, 65 90, 55 90 C 73 102, 100 120, 100 150 Z" fill="#FACC15" />
      <path d="M 100 150 C 100 115, 135 90, 145 90 C 127 102, 100 120, 100 150 Z" fill="#FACC15" />
      <path d="M 100 150 C 100 105, 82 70, 72 70 C 86 82, 100 110, 100 150 Z" fill="#4ADE80" />
      <path d="M 100 150 C 100 105, 118 70, 128 70 C 114 82, 100 110, 100 150 Z" fill="#4ADE80" />
      <path d="M 100 150 C 100 90, 100 50, 100 50" stroke="#EAB308" strokeWidth="10" strokeLinecap="round" />
    </svg>
  ),

  // ==================== Sasol ====================
  "sasol": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#0F172A" />
      <circle cx="100" cy="100" r="28" fill="#0EA5E9" />
      <g stroke="#FFFFFF" strokeWidth="2.5">
        <line x1="100" y1="100" x2="100" y2="50" />
        <line x1="100" y1="100" x2="100" y2="150" />
        <line x1="100" y1="100" x2="50" y2="100" />
        <line x1="100" y1="100" x2="150" y2="100" />
        <line x1="100" y1="100" x2="65" y2="65" />
        <line x1="100" y1="100" x2="135" y2="135" />
        <line x1="100" y1="100" x2="135" y2="65" />
        <line x1="100" y1="100" x2="65" y2="135" />
      </g>
      <circle cx="100" cy="50" r="10" fill="#38BDF8" />
      <circle cx="100" cy="150" r="10" fill="#0284C7" />
      <circle cx="50" cy="100" r="10" fill="#0284C7" />
      <circle cx="150" cy="100" r="10" fill="#38BDF8" />
      <circle cx="65" cy="65" r="10" fill="#38BDF8" />
      <circle cx="135" cy="135" r="10" fill="#0284C7" />
      <circle cx="135" cy="65" r="10" fill="#7DD3FC" />
      <circle cx="65" cy="135" r="10" fill="#0C4A6E" />
    </svg>
  ),
  "sasol.com": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#0F172A" />
      <circle cx="100" cy="100" r="28" fill="#0EA5E9" />
      <g stroke="#FFFFFF" strokeWidth="2.5">
        <line x1="100" y1="100" x2="100" y2="50" />
        <line x1="100" y1="100" x2="100" y2="150" />
        <line x1="100" y1="100" x2="50" y2="100" />
        <line x1="100" y1="100" x2="150" y2="100" />
        <line x1="100" y1="100" x2="65" y2="65" />
        <line x1="100" y1="100" x2="135" y2="135" />
        <line x1="100" y1="100" x2="135" y2="65" />
        <line x1="100" y1="100" x2="65" y2="135" />
      </g>
      <circle cx="100" cy="50" r="10" fill="#38BDF8" />
      <circle cx="100" cy="150" r="10" fill="#0284C7" />
      <circle cx="50" cy="100" r="10" fill="#0284C7" />
      <circle cx="150" cy="100" r="10" fill="#38BDF8" />
      <circle cx="65" cy="65" r="10" fill="#38BDF8" />
      <circle cx="135" cy="135" r="10" fill="#0284C7" />
      <circle cx="135" cy="65" r="10" fill="#7DD3FC" />
      <circle cx="65" cy="135" r="10" fill="#0C4A6E" />
    </svg>
  ),

  // ==================== China Northern Rare Earth ====================
  "china northern rare earth": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#022C22" />
      <ellipse cx="100" cy="100" rx="65" ry="25" stroke="#10B981" strokeWidth="4" transform="rotate(-30 100 100)" fill="none" strokeDasharray="16 8" />
      <ellipse cx="100" cy="100" rx="65" ry="25" stroke="#059669" strokeWidth="4" transform="rotate(30 100 100)" fill="none" />
      <circle cx="100" cy="100" r="24" fill="#34D399" />
      <circle cx="100" cy="100" r="10" fill="#F0FDF4" />
      <text x="100" y="162" fill="#10B981" fontSize="20" fontWeight="900" fontFamily="monospace" textAnchor="middle" letterSpacing="2">RARE-EA</text>
    </svg>
  ),
  "china northern": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#022C22" />
      <ellipse cx="100" cy="100" rx="65" ry="25" stroke="#10B981" strokeWidth="4" transform="rotate(-30 100 100)" fill="none" strokeDasharray="16 8" />
      <ellipse cx="100" cy="100" rx="65" ry="25" stroke="#059669" strokeWidth="4" transform="rotate(30 100 100)" fill="none" />
      <circle cx="100" cy="100" r="24" fill="#34D399" />
      <circle cx="100" cy="100" r="10" fill="#F0FDF4" />
      <text x="100" y="162" fill="#10B981" fontSize="20" fontWeight="900" fontFamily="monospace" textAnchor="middle" letterSpacing="2">RARE-EA</text>
    </svg>
  ),
  "re-northern.com": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#022C22" />
      <ellipse cx="100" cy="100" rx="65" ry="25" stroke="#10B981" strokeWidth="4" transform="rotate(-30 100 100)" fill="none" strokeDasharray="16 8" />
      <ellipse cx="100" cy="100" rx="65" ry="25" stroke="#059669" strokeWidth="4" transform="rotate(30 100 100)" fill="none" />
      <circle cx="100" cy="100" r="24" fill="#34D399" />
      <circle cx="100" cy="100" r="10" fill="#F0FDF4" />
      <text x="100" y="162" fill="#10B981" fontSize="20" fontWeight="900" fontFamily="monospace" textAnchor="middle" letterSpacing="2">RARE-EA</text>
    </svg>
  ),
  "reht.com": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#022C22" />
      <ellipse cx="100" cy="100" rx="65" ry="25" stroke="#10B981" strokeWidth="4" transform="rotate(-30 100 100)" fill="none" strokeDasharray="16 8" />
      <ellipse cx="100" cy="100" rx="65" ry="25" stroke="#059669" strokeWidth="4" transform="rotate(30 100 100)" fill="none" />
      <circle cx="100" cy="100" r="24" fill="#34D399" />
      <circle cx="100" cy="100" r="10" fill="#F0FDF4" />
      <text x="100" y="162" fill="#10B981" fontSize="20" fontWeight="900" fontFamily="monospace" textAnchor="middle" letterSpacing="2">RARE-EA</text>
    </svg>
  ),

  // ==================== Evergrande ====================
  "evergrande": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#0F172A" />
      <circle cx="100" cy="100" r="62" stroke="#D97706" strokeWidth="8" fill="none" />
      <path d="M 65 130 L 100 60 L 135 130 Z" fill="#DC2626" />
      <path d="M 100 60 L 118 95 L 100 130 L 82 95 Z" fill="#2563EB" />
      <path d="M 100 30 L 100 170" stroke="#F59E0B" strokeWidth="3" />
    </svg>
  ),
  "china evergrande group": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#0F172A" />
      <circle cx="100" cy="100" r="62" stroke="#D97706" strokeWidth="8" fill="none" />
      <path d="M 65 130 L 100 60 L 135 130 Z" fill="#DC2626" />
      <path d="M 100 60 L 118 95 L 100 130 L 82 95 Z" fill="#2563EB" />
      <path d="M 100 30 L 100 170" stroke="#F59E0B" strokeWidth="3" />
    </svg>
  ),
  "china evergrande": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#0F172A" />
      <circle cx="100" cy="100" r="62" stroke="#D97706" strokeWidth="8" fill="none" />
      <path d="M 65 130 L 100 60 L 135 130 Z" fill="#DC2626" />
      <path d="M 100 60 L 118 95 L 100 130 L 82 95 Z" fill="#2563EB" />
      <path d="M 100 30 L 100 170" stroke="#F59E0B" strokeWidth="3" />
    </svg>
  ),
  "evergrande.com": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#0F172A" />
      <circle cx="100" cy="100" r="62" stroke="#D97706" strokeWidth="8" fill="none" />
      <path d="M 65 130 L 100 60 L 135 130 Z" fill="#DC2626" />
      <path d="M 100 60 L 118 95 L 100 130 L 82 95 Z" fill="#2563EB" />
      <path d="M 100 30 L 100 170" stroke="#F59E0B" strokeWidth="3" />
    </svg>
  ),

  // ==================== Tokyo Electric Power ====================
  "tokyo electric power": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#DC2626" />
      <circle cx="100" cy="100" r="55" stroke="#FFFFFF" strokeWidth="12" fill="none" />
      <path d="M 100 55 L 100 145 M 55 100 L 145 100" stroke="#FFFFFF" strokeWidth="10" strokeLinecap="round" />
      <circle cx="100" cy="100" r="14" fill="#DC2626" />
    </svg>
  ),
  "tepco": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#DC2626" />
      <circle cx="100" cy="100" r="55" stroke="#FFFFFF" strokeWidth="12" fill="none" />
      <path d="M 100 55 L 100 145 M 55 100 L 145 100" stroke="#FFFFFF" strokeWidth="10" strokeLinecap="round" />
      <circle cx="100" cy="100" r="14" fill="#DC2626" />
    </svg>
  ),
  "tepco.co.jp": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#DC2626" />
      <circle cx="100" cy="100" r="55" stroke="#FFFFFF" strokeWidth="12" fill="none" />
      <path d="M 100 55 L 100 145 M 55 100 L 145 100" stroke="#FFFFFF" strokeWidth="10" strokeLinecap="round" />
      <circle cx="100" cy="100" r="14" fill="#DC2626" />
    </svg>
  ),

  // ==================== Yanzhou Coal Mining ====================
  "yanzhou coal mining": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#292524" />
      <circle cx="100" cy="100" r="55" stroke="#F97316" strokeWidth="8" />
      <path d="M 75 135 L 100 75 L 125 135 Z" fill="#EF4444" />
      <path d="M 100 45 C 108 45, 120 62, 116 80 C 112 98, 100 108, 100 108 C 100 108, 88 98, 84 80 C 80 62, 92 45, 100 45 Z" fill="#FACC15" />
    </svg>
  ),
  "yanzhoucoal.com.cn": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#292524" />
      <circle cx="100" cy="100" r="55" stroke="#F97316" strokeWidth="8" />
      <path d="M 75 135 L 100 75 L 125 135 Z" fill="#EF4444" />
      <path d="M 100 45 C 108 45, 120 62, 116 80 C 112 98, 100 108, 100 108 C 100 108, 88 98, 84 80 C 80 62, 92 45, 100 45 Z" fill="#FACC15" />
    </svg>
  ),

  // ==================== Anhui Conch Cement ====================
  "anhui conch cement": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#0369A1" />
      <path d="M 100 40 C 133 40, 160 67, 160 100 C 160 115, 150 130, 135 140 C 120 150, 80 160, 60 130 C 45 110, 50 80, 70 60 C 85 45, 115 50, 120 70 C 125 80, 110 95, 95 90 C 85 85, 90 75, 100 75" stroke="#FFFFFF" strokeWidth="12" strokeLinecap="round" fill="none" />
      <circle cx="100" cy="100" r="14" fill="#F97316" />
    </svg>
  ),
  "conch.cn": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#0369A1" />
      <path d="M 100 40 C 133 40, 160 67, 160 100 C 160 115, 150 130, 135 140 C 120 150, 80 160, 60 130 C 45 110, 50 80, 70 60 C 85 45, 115 50, 120 70 C 125 80, 110 95, 95 90 C 85 85, 90 75, 100 75" stroke="#FFFFFF" strokeWidth="12" strokeLinecap="round" fill="none" />
      <circle cx="100" cy="100" r="14" fill="#F97316" />
    </svg>
  ),

  // ==================== CNBM ====================
  "cnbm": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#065F46" />
      <rect x="50" y="50" width="38" height="38" rx="6" fill="#10B981" />
      <rect x="112" y="50" width="38" height="38" rx="6" fill="#6EE7B7" />
      <rect x="50" y="112" width="38" height="38" rx="6" fill="#047857" />
      <rect x="112" y="112" width="38" height="38" rx="6" fill="#3B82F6" />
    </svg>
  ),
  "cnbm.com.cn": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#065F46" />
      <rect x="50" y="50" width="38" height="38" rx="6" fill="#10B981" />
      <rect x="112" y="50" width="38" height="38" rx="6" fill="#6EE7B7" />
      <rect x="50" y="112" width="38" height="38" rx="6" fill="#047857" />
      <rect x="112" y="112" width="38" height="38" rx="6" fill="#3B82F6" />
    </svg>
  ),

  // ==================== Sunac China ====================
  "sunac china": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#1E3A8A" />
      <path d="M 50 140 A 50 50 0 0 1 150 140" stroke="#F59E0B" strokeWidth="12" strokeLinecap="round" />
      <circle cx="100" cy="90" r="20" fill="#3B82F6" />
      <circle cx="100" cy="90" r="9" fill="#FBBF24" />
    </svg>
  ),
  "sunac.com.cn": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#1E3A8A" />
      <path d="M 50 140 A 50 50 0 0 1 150 140" stroke="#F59E0B" strokeWidth="12" strokeLinecap="round" />
      <circle cx="100" cy="90" r="20" fill="#3B82F6" />
      <circle cx="100" cy="90" r="9" fill="#FBBF24" />
    </svg>
  ),

  // ==================== Poly Developments ====================
  "poly developments": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#991B1B" />
      <path d="M 60 50 L 125 50 A 35 35 0 0 1 125 120 L 95 120 C 95 120, 95 140, 95 155" stroke="#FBBF24" strokeWidth="12" strokeLinecap="round" fill="none" />
      <polygon points="95,70 115,92 95,114" fill="#FFFFFF" />
    </svg>
  ),
  "poly.com.cn": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#991B1B" />
      <path d="M 60 50 L 125 50 A 35 35 0 0 1 125 120 L 95 120 C 95 120, 95 140, 95 155" stroke="#FBBF24" strokeWidth="12" strokeLinecap="round" fill="none" />
      <polygon points="95,70 115,92 95,114" fill="#FFFFFF" />
    </svg>
  ),

  // ==================== Industrial & Commercial Bank of China ====================
  "industrial & commercial bank of china": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#DC2626" />
      <circle cx="100" cy="100" r="50" stroke="#FFFFFF" strokeWidth="12" fill="none" />
      <rect x="86" y="86" width="28" height="28" fill="#FFFFFF" />
      <path d="M 100 45 L 100 155" stroke="#FFFFFF" strokeWidth="12" strokeLinecap="round" />
    </svg>
  ),
  "icbc.com.cn": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#DC2626" />
      <circle cx="100" cy="100" r="50" stroke="#FFFFFF" strokeWidth="12" fill="none" />
      <rect x="86" y="86" width="28" height="28" fill="#FFFFFF" />
      <path d="M 100 45 L 100 155" stroke="#FFFFFF" strokeWidth="12" strokeLinecap="round" />
    </svg>
  ),

  // ==================== Bank of China ====================
  "bank of china": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#B91C1C" />
      <circle cx="100" cy="100" r="48" stroke="#FFFFFF" strokeWidth="10" fill="none" />
      <rect x="87" y="87" width="26" height="26" stroke="#FFFFFF" strokeWidth="10" fill="none" />
      <path d="M 100 40 L 100 160" stroke="#FFFFFF" strokeWidth="10" />
    </svg>
  ),
  "boc.cn": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#B91C1C" />
      <circle cx="100" cy="100" r="48" stroke="#FFFFFF" strokeWidth="10" fill="none" />
      <rect x="87" y="87" width="26" height="26" stroke="#FFFFFF" strokeWidth="10" fill="none" />
      <path d="M 100 40 L 100 160" stroke="#FFFFFF" strokeWidth="10" />
    </svg>
  ),

  // ==================== China Life Insurance ====================
  "china life insurance": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#15803D" />
      <circle cx="100" cy="100" r="55" fill="none" stroke="#FFFFFF" strokeWidth="8" />
      <path d="M 100 52 C 118 52, 135 70, 135 100 C 135 128, 118 148, 100 148 C 82 148, 65 128, 65 100 C 65 70, 82 52, 100 52 Z" fill="#FFFFFF" />
      <circle cx="100" cy="100" r="24" fill="#15803D" />
    </svg>
  ),
  "chinalife.com.cn": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#15803D" />
      <circle cx="100" cy="100" r="55" fill="none" stroke="#FFFFFF" strokeWidth="8" />
      <path d="M 100 52 C 118 52, 135 70, 135 100 C 135 128, 118 148, 100 148 C 82 148, 65 128, 65 100 C 65 70, 82 52, 100 52 Z" fill="#FFFFFF" />
      <circle cx="100" cy="100" r="24" fill="#15803D" />
    </svg>
  ),

  // ==================== ZTE ====================
  "zte": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#1D4ED8" />
      <path d="M 50 135 L 50 65 L 105 135 L 105 65" stroke="#FFFFFF" strokeWidth="14" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="140" cy="75" r="14" fill="#60A5FA" />
    </svg>
  ),
  "zte.com.cn": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#1D4ED8" />
      <path d="M 50 135 L 50 65 L 105 135 L 105 65" stroke="#FFFFFF" strokeWidth="14" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="140" cy="75" r="14" fill="#60A5FA" />
    </svg>
  ),

  // ==================== AIA GROUP ====================
  "aia": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#D31115" />
      <path d="M 100 45 L 160 155 L 125 155 L 100 110 L 75 155 L 40 155 Z" fill="#FFFFFF" />
    </svg>
  ),
  "aia.com": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#D31115" />
      <path d="M 100 45 L 160 155 L 125 155 L 100 110 L 75 155 L 40 155 Z" fill="#FFFFFF" />
    </svg>
  ),
  "aia group": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#D31115" />
      <path d="M 100 45 L 160 155 L 125 155 L 100 110 L 75 155 L 40 155 Z" fill="#FFFFFF" />
    </svg>
  ),

  // ==================== ANT GROUP ====================
  "antgroup": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#1677FF" />
      <circle cx="100" cy="115" r="30" fill="#FFFFFF" />
      <circle cx="100" cy="65" r="18" fill="#FFFFFF" />
      <path d="M 85 45 C 85 25, 65 30, 65 30" stroke="#FFFFFF" strokeWidth="8" strokeLinecap="round" fill="none" />
      <path d="M 115 45 C 115 25, 135 30, 135 30" stroke="#FFFFFF" strokeWidth="8" strokeLinecap="round" fill="none" />
      <circle cx="88" cy="62" r="4" fill="#1677FF" />
      <circle cx="112" cy="62" r="4" fill="#1677FF" />
    </svg>
  ),
  "antgroup.com": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#1677FF" />
      <circle cx="100" cy="115" r="30" fill="#FFFFFF" />
      <circle cx="100" cy="65" r="18" fill="#FFFFFF" />
      <path d="M 85 45 C 85 25, 65 30, 65 30" stroke="#FFFFFF" strokeWidth="8" strokeLinecap="round" fill="none" />
      <path d="M 115 45 C 115 25, 135 30, 135 30" stroke="#FFFFFF" strokeWidth="8" strokeLinecap="round" fill="none" />
      <circle cx="88" cy="62" r="4" fill="#1677FF" />
      <circle cx="112" cy="62" r="4" fill="#1677FF" />
    </svg>
  ),
  "ant group": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#1677FF" />
      <circle cx="100" cy="115" r="30" fill="#FFFFFF" />
      <circle cx="100" cy="65" r="18" fill="#FFFFFF" />
      <path d="M 85 45 C 85 25, 65 30, 65 30" stroke="#FFFFFF" strokeWidth="8" strokeLinecap="round" fill="none" />
      <path d="M 115 45 C 115 25, 135 30, 135 30" stroke="#FFFFFF" strokeWidth="8" strokeLinecap="round" fill="none" />
      <circle cx="88" cy="62" r="4" fill="#1677FF" />
      <circle cx="112" cy="62" r="4" fill="#1677FF" />
    </svg>
  ),

  // ==================== BYTEDANCE ====================
  "bytedance": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#0F172A" />
      <rect x="52" y="70" width="18" height="60" rx="9" fill="#00D2C4" />
      <rect x="82" y="45" width="18" height="110" rx="9" fill="#3824E3" />
      <rect x="112" y="60" width="18" height="80" rx="9" fill="#00D2C4" />
      <rect x="142" y="85" width="18" height="30" rx="9" fill="#3824E3" />
    </svg>
  ),
  "bytedance.com": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#0F172A" />
      <rect x="52" y="70" width="18" height="60" rx="9" fill="#00D2C4" />
      <rect x="82" y="45" width="18" height="110" rx="9" fill="#3824E3" />
      <rect x="112" y="60" width="18" height="80" rx="9" fill="#00D2C4" />
      <rect x="142" y="85" width="18" height="30" rx="9" fill="#3824E3" />
    </svg>
  ),
  "byte dance": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#0F172A" />
      <rect x="52" y="70" width="18" height="60" rx="9" fill="#00D2C4" />
      <rect x="82" y="45" width="18" height="110" rx="9" fill="#3824E3" />
      <rect x="112" y="60" width="18" height="80" rx="9" fill="#00D2C4" />
      <rect x="142" y="85" width="18" height="30" rx="9" fill="#3824E3" />
    </svg>
  ),

  // ==================== SINOPHARM ====================
  "sinopharm": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#004EA2" />
      <circle cx="100" cy="100" r="55" stroke="#FFFFFF" strokeWidth="8" fill="none" />
      <path d="M 75 100 C 75 80, 125 80, 125 100 C 125 120, 75 120, 75 100 Z" fill="#E31E24" opacity="0.85" />
      <path d="M 125 100 C 125 80, 75 80, 75 100 C 75 120, 125 120, 125 100 Z" fill="#FFFFFF" opacity="0.85" />
      <text x="100" y="155" fill="#FFFFFF" fontSize="22" fontWeight="800" fontFamily="sans-serif" textAnchor="middle">SINOPHARM</text>
    </svg>
  ),
  "sinopharm.com": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#004EA2" />
      <circle cx="100" cy="100" r="55" stroke="#FFFFFF" strokeWidth="8" fill="none" />
      <path d="M 75 100 C 75 80, 125 80, 125 100 C 125 120, 75 120, 75 100 Z" fill="#E31E24" opacity="0.85" />
      <path d="M 125 100 C 125 80, 75 80, 75 100 C 75 120, 125 120, 125 100 Z" fill="#FFFFFF" opacity="0.85" />
      <text x="100" y="155" fill="#FFFFFF" fontSize="22" fontWeight="800" fontFamily="sans-serif" textAnchor="middle">SINOPHARM</text>
    </svg>
  ),

  // ==================== STATE GRID ====================
  "sgcc": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#006F53" />
      <circle cx="100" cy="100" r="50" stroke="#FFFFFF" strokeWidth="12" fill="none" />
      <path d="M 60 100 L 140 100 M 100 60 L 100 140" stroke="#FFFFFF" strokeWidth="8" />
      <path d="M 68 80 C 85 92, 115 92, 132 80" stroke="#FFFFFF" strokeWidth="6" fill="none" />
      <path d="M 68 120 C 85 108, 115 108, 132 120" stroke="#FFFFFF" strokeWidth="6" fill="none" />
    </svg>
  ),
  "sgcc.com.cn": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#006F53" />
      <circle cx="100" cy="100" r="50" stroke="#FFFFFF" strokeWidth="12" fill="none" />
      <path d="M 60 100 L 140 100 M 100 60 L 100 140" stroke="#FFFFFF" strokeWidth="8" />
      <path d="M 68 80 C 85 92, 115 92, 132 80" stroke="#FFFFFF" strokeWidth="6" fill="none" />
      <path d="M 68 120 C 85 108, 115 108, 132 120" stroke="#FFFFFF" strokeWidth="6" fill="none" />
    </svg>
  ),

  // ==================== CNPC ====================
  "cnpc": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#EAB308" />
      <circle cx="100" cy="100" r="60" fill="#DC2626" />
      <polygon points="100,60 112,95 145,100 112,105 100,140 88,105 55,100 88,95" fill="#FBBF24" />
      <circle cx="100" cy="100" r="14" fill="#DC2626" />
    </svg>
  ),
  "cnpc.com.cn": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#EAB308" />
      <circle cx="100" cy="100" r="60" fill="#DC2626" />
      <polygon points="100,60 112,95 145,100 112,105 100,140 88,105 55,100 88,95" fill="#FBBF24" />
      <circle cx="100" cy="100" r="14" fill="#DC2626" />
    </svg>
  ),

  // ==================== SHENHUA ENERGY ====================
  "shenhua": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#1E293B" />
      <circle cx="100" cy="100" r="55" stroke="#EF4444" strokeWidth="10" />
      <path d="M 70 140 L 100 70 L 130 140" fill="#FBBF24" />
      <circle cx="100" cy="100" r="18" fill="#EF4444" />
    </svg>
  ),
  "csh.com.cn": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#1E293B" />
      <circle cx="100" cy="100" r="55" stroke="#EF4444" strokeWidth="10" />
      <path d="M 70 140 L 100 70 L 130 140" fill="#FBBF24" />
      <circle cx="100" cy="100" r="18" fill="#EF4444" />
    </svg>
  ),

  // ==================== ZIJIN MINING ====================
  "zijin": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#78350F" />
      <path d="M 100 40 L 155 95 L 100 150 L 45 95 Z" fill="#F59E0B" />
      <circle cx="100" cy="95" r="18" fill="#78350F" />
    </svg>
  ),
  "zijinmining.com": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#78350F" />
      <path d="M 100 40 L 155 95 L 100 150 L 45 95 Z" fill="#F59E0B" />
      <circle cx="100" cy="95" r="18" fill="#78350F" />
    </svg>
  ),

  // ==================== ESKOM ====================
  "eskom": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#0284C7" />
      <path d="M 50 70 L 150 70 M 50 100 L 150 100 M 50 130 L 150 130" stroke="#FFFFFF" strokeWidth="16" strokeLinecap="round" />
      <path d="M 100 40 L 100 160" stroke="#0369A1" strokeWidth="10" />
    </svg>
  ),
  "eskom.co.za": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#0284C7" />
      <path d="M 50 70 L 150 70 M 50 100 L 150 100 M 50 130 L 150 130" stroke="#FFFFFF" strokeWidth="16" strokeLinecap="round" />
      <path d="M 100 40 L 100 160" stroke="#0369A1" strokeWidth="10" />
    </svg>
  ),

  // ==================== DANGOTE GROUP ====================
  "dangote": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#0B3C5D" />
      <text x="100" y="115" fill="#F59E0B" fontSize="72" fontWeight="900" fontFamily="sans-serif" textAnchor="middle">D</text>
      <circle cx="100" cy="100" r="70" stroke="#F59E0B" strokeWidth="8" fill="none" />
    </svg>
  ),
  "dangote.com": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#0B3C5D" />
      <text x="100" y="115" fill="#F59E0B" fontSize="72" fontWeight="900" fontFamily="sans-serif" textAnchor="middle">D</text>
      <circle cx="100" cy="100" r="70" stroke="#F59E0B" strokeWidth="8" fill="none" />
    </svg>
  ),

  // ==================== OCP ====================
  "ocp": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#15803D" />
      <circle cx="100" cy="100" r="50" fill="none" stroke="#FBBF24" strokeWidth="10" />
      <path d="M 100 60 A 40 40 0 0 1 140 100 L 100 100 Z" fill="#FBBF24" />
    </svg>
  ),
  "ocpgroup.ma": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#15803D" />
      <circle cx="100" cy="100" r="50" fill="none" stroke="#FBBF24" strokeWidth="10" />
      <path d="M 100 60 A 40 40 0 0 1 140 100 L 100 100 Z" fill="#FBBF24" />
    </svg>
  ),

  // ==================== ADANI GROUP ====================
  "adani": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#0C2340" />
      <path d="M 40 140 C 60 70, 140 70, 160 140" stroke="#22C55E" strokeWidth="16" strokeLinecap="round" fill="none" />
      <path d="M 60 140 C 80 90, 120 90, 140 140" stroke="#EF4444" strokeWidth="12" strokeLinecap="round" fill="none" />
      <circle cx="100" cy="65" r="15" fill="#EAB308" />
    </svg>
  ),
  "adani.com": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#0C2340" />
      <path d="M 40 140 C 60 70, 140 70, 160 140" stroke="#22C55E" strokeWidth="16" strokeLinecap="round" fill="none" />
      <path d="M 60 140 C 80 90, 120 90, 140 140" stroke="#EF4444" strokeWidth="12" strokeLinecap="round" fill="none" />
      <circle cx="100" cy="65" r="15" fill="#EAB308" />
    </svg>
  ),

  // ==================== RELIANCE INDUSTRIES ====================
  "reliance": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#0A2540" />
      <circle cx="100" cy="100" r="55" stroke="#DC2626" strokeWidth="14" fill="none" />
      <text x="100" y="118" fill="#FFFFFF" fontSize="56" fontWeight="bold" fontFamily="sans-serif" textAnchor="middle">R</text>
    </svg>
  ),
  "ril.com": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#0A2540" />
      <circle cx="100" cy="100" r="55" stroke="#DC2626" strokeWidth="14" fill="none" />
      <text x="100" y="118" fill="#FFFFFF" fontSize="56" fontWeight="bold" fontFamily="sans-serif" textAnchor="middle">R</text>
    </svg>
  ),

  // ==================== TATA STEEL ====================
  "tata": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#0F4C81" />
      <path d="M 100 45 C 55 45, 55 85, 55 105 C 55 145, 100 155, 100 155 C 100 155, 145 145, 145 105 C 145 85, 145 45, 100 45 Z" fill="#FFFFFF" />
      <path d="M 70 70 L 130 70 M 100 70 L 100 130" stroke="#0F4C81" strokeWidth="16" strokeLinecap="round" />
    </svg>
  ),
  "tatasteel.com": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#0F4C81" />
      <path d="M 100 45 C 55 45, 55 85, 55 105 C 55 145, 100 155, 100 155 C 100 155, 145 145, 145 105 C 145 85, 145 45, 100 45 Z" fill="#FFFFFF" />
      <path d="M 70 70 L 130 70 M 100 70 L 100 130" stroke="#0F4C81" strokeWidth="16" strokeLinecap="round" />
    </svg>
  ),

  // ==================== JSW STEEL ====================
  "jsw": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#0284C7" />
      <text x="100" y="120" fill="#FFFFFF" fontSize="64" fontWeight="bold" fontFamily="sans-serif" textAnchor="middle">JSW</text>
      <path d="M 40 140 H 160" stroke="#EF4444" strokeWidth="12" strokeLinecap="round" />
    </svg>
  ),
  "jsw.in": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#0284C7" />
      <text x="100" y="120" fill="#FFFFFF" fontSize="64" fontWeight="bold" fontFamily="sans-serif" textAnchor="middle">JSW</text>
      <path d="M 40 140 H 160" stroke="#EF4444" strokeWidth="12" strokeLinecap="round" />
    </svg>
  ),

  // ==================== HINDALCO ====================
  "hindalco": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#065F46" />
      <polygon points="100,45 155,145 45,145" stroke="#FFFFFF" strokeWidth="12" fill="none" strokeLinejoin="round" />
      <circle cx="100" cy="105" r="14" fill="#059669" />
    </svg>
  ),
  "hindalco.com": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#065F46" />
      <polygon points="100,45 155,145 45,145" stroke="#FFFFFF" strokeWidth="12" fill="none" strokeLinejoin="round" />
      <circle cx="100" cy="105" r="14" fill="#059669" />
    </svg>
  ),

  // ==================== INDIAN OIL ====================
  "iocl": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#EA580C" />
      <circle cx="100" cy="100" r="55" stroke="#FFFFFF" strokeWidth="12" fill="none" />
      <rect x="70" y="88" width="60" height="24" fill="#FFFFFF" rx="4" />
      <circle cx="100" cy="100" r="14" fill="#FFFFFF" />
    </svg>
  ),
  "iocl.com": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#EA580C" />
      <circle cx="100" cy="100" r="55" stroke="#FFFFFF" strokeWidth="12" fill="none" />
      <rect x="70" y="88" width="60" height="24" fill="#FFFFFF" rx="4" />
      <circle cx="100" cy="100" r="14" fill="#FFFFFF" />
    </svg>
  ),

  // ==================== ONGC ====================
  "ongc": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#B91C1C" />
      <text x="100" y="120" fill="#FBBF24" fontSize="56" fontWeight="900" fontFamily="sans-serif" textAnchor="middle">ONGC</text>
      <path d="M 40 145 H 160" stroke="#FBBF24" strokeWidth="8" strokeLinecap="round" />
    </svg>
  ),
  "ongcindia.com": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#B91C1C" />
      <text x="100" y="120" fill="#FBBF24" fontSize="56" fontWeight="900" fontFamily="sans-serif" textAnchor="middle">ONGC</text>
      <path d="M 40 145 H 160" stroke="#FBBF24" strokeWidth="8" strokeLinecap="round" />
    </svg>
  ),

  // ==================== BHARAT PETROLEUM ====================
  "bharatpetroleum": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#1D4ED8" />
      <circle cx="100" cy="90" r="35" fill="#FBBF24" />
      <path d="M 60 140 C 80 110, 120 110, 140 140" fill="#F97316" />
    </svg>
  ),
  "bharatpetroleum.in": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#1D4ED8" />
      <circle cx="100" cy="90" r="35" fill="#FBBF24" />
      <path d="M 60 140 C 80 110, 120 110, 140 140" fill="#F97316" />
    </svg>
  ),

  // ==================== PETRONAS ====================
  "petronas": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#00A19C" />
      <path d="M 100 40 C 70 70, 60 110, 100 160 C 140 110, 130 70, 100 40 Z" fill="#FFFFFF" />
      <circle cx="100" cy="100" r="18" fill="#00A19C" />
      <polygon points="100,70 120,110 80,110" fill="#00A19C" />
    </svg>
  ),
  "petronas.com": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#00A19C" />
      <path d="M 100 40 C 70 70, 60 110, 100 160 C 140 110, 130 70, 100 40 Z" fill="#FFFFFF" />
      <circle cx="100" cy="100" r="18" fill="#00A19C" />
      <polygon points="100,70 120,110 80,110" fill="#00A19C" />
    </svg>
  ),

  // ==================== PERTAMINA ====================
  "pertamina": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#FFFFFF" />
      <path d="M 50 65 L 100 145 L 60 145 Z" fill="#DC2626" />
      <path d="M 100 65 L 150 145 L 110 145 Z" fill="#16A34A" />
      <path d="M 75 45 H 125 L 100 85 Z" fill="#2563EB" />
    </svg>
  ),
  "pertamina.com": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#FFFFFF" />
      <path d="M 50 65 L 100 145 L 60 145 Z" fill="#DC2626" />
      <path d="M 100 65 L 150 145 L 110 145 Z" fill="#16A34A" />
      <path d="M 75 45 H 125 L 100 85 Z" fill="#2563EB" />
    </svg>
  ),

  // ==================== PTT ====================
  "ptt": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#025EA9" />
      <circle cx="100" cy="100" r="45" fill="none" stroke="#F97316" strokeWidth="12" />
      <circle cx="100" cy="100" r="14" fill="#FFFFFF" />
    </svg>
  ),
  "pttplc.com": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#025EA9" />
      <circle cx="100" cy="100" r="45" fill="none" stroke="#F97316" strokeWidth="12" />
      <circle cx="100" cy="100" r="14" fill="#FFFFFF" />
    </svg>
  ),

  // ==================== CHINA STATE CONSTRUCTION ====================
  "cscec": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#004EA2" />
      <text x="100" y="115" fill="#FFFFFF" fontSize="50" fontWeight="900" fontFamily="sans-serif" textAnchor="middle">CSCEC</text>
      <rect x="40" y="130" width="120" height="12" fill="#FFFFFF" rx="4" />
    </svg>
  ),
  "cscec.com": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#004EA2" />
      <text x="100" y="115" fill="#FFFFFF" fontSize="50" fontWeight="900" fontFamily="sans-serif" textAnchor="middle">CSCEC</text>
      <rect x="40" y="130" width="120" height="12" fill="#FFFFFF" rx="4" />
    </svg>
  ),

  // ==================== CHINA RAILWAY CONSTRUCTION ====================
  "crcc": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#C2410C" />
      <circle cx="100" cy="100" r="50" stroke="#FFFFFF" strokeWidth="12" fill="none" />
      <path d="M 70 85 L 100 125 L 130 85" stroke="#FFFFFF" strokeWidth="12" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  ),
  "crcc.cn": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#C2410C" />
      <circle cx="100" cy="100" r="50" stroke="#FFFFFF" strokeWidth="12" fill="none" />
      <path d="M 70 85 L 100 125 L 130 85" stroke="#FFFFFF" strokeWidth="12" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  ),

  // ==================== CHINA COMMUNICATIONS CONSTRUCTION ====================
  "cccc": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#0B3C5D" />
      <text x="100" y="120" fill="#FFFFFF" fontSize="64" fontWeight="900" fontFamily="sans-serif" textAnchor="middle">CCCC</text>
    </svg>
  ),
  "cccc.com.cn": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#0B3C5D" />
      <text x="100" y="120" fill="#FFFFFF" fontSize="64" fontWeight="900" fontFamily="sans-serif" textAnchor="middle">CCCC</text>
    </svg>
  ),

  // ==================== COUNTRY GARDEN ====================
  "bgy": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#D97706" />
      <path d="M 60 140 L 100 60 L 140 140 Z" fill="#DC2626" />
      <circle cx="100" cy="85" r="18" fill="#FBBF24" />
    </svg>
  ),
  "bgy.com.cn": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#D97706" />
      <path d="M 60 140 L 100 60 L 140 140 Z" fill="#DC2626" />
      <circle cx="100" cy="85" r="18" fill="#FBBF24" />
    </svg>
  ),

  // ==================== PING AN INSURANCE ====================
  "pingan": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#EA580C" />
      <circle cx="100" cy="100" r="50" stroke="#16A34A" strokeWidth="14" fill="none" />
      <text x="100" y="118" fill="#FFFFFF" fontSize="56" fontWeight="bold" fontFamily="sans-serif" textAnchor="middle">PA</text>
    </svg>
  ),
  "pingan.com": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#EA580C" />
      <circle cx="100" cy="100" r="50" stroke="#16A34A" strokeWidth="14" fill="none" />
      <text x="100" y="118" fill="#FFFFFF" fontSize="56" fontWeight="bold" fontFamily="sans-serif" textAnchor="middle">PA</text>
    </svg>
  ),

  // ==================== SOFTBANK ====================
  "softbank": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#1E293B" />
      <rect x="50" y="65" width="100" height="20" fill="#FFFFFF" />
      <rect x="50" y="105" width="100" height="20" fill="#FFFFFF" />
    </svg>
  ),
  "softbank.jp": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#1E293B" />
      <rect x="50" y="65" width="100" height="20" fill="#FFFFFF" />
      <rect x="50" y="105" width="100" height="20" fill="#FFFFFF" />
    </svg>
  ),

  // ==================== DIDI GLOBAL ====================
  "didi": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#FFFFFF" />
      <path d="M 145 100 A 45 45 0 1 1 100 55" stroke="#EA580C" strokeWidth="16" strokeLinecap="round" fill="none" />
      <circle cx="100" cy="100" r="12" fill="#EA580C" />
    </svg>
  ),
  "didiglobal.com": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#FFFFFF" />
      <path d="M 145 100 A 45 45 0 1 1 100 55" stroke="#EA580C" strokeWidth="16" strokeLinecap="round" fill="none" />
      <circle cx="100" cy="100" r="12" fill="#EA580C" />
    </svg>
  ),

  // ==================== WEBHELP ====================
  "webhelp": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#1E1B4B" />
      <circle cx="100" cy="90" r="45" fill="none" stroke="#FBBF24" strokeWidth="10" />
      <path d="M 75 75 L 90 115 L 100 90 L 110 115 L 125 75" stroke="#FFFFFF" strokeWidth="10" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <text x="100" y="165" fill="#FBBF24" fontSize="18" fontWeight="800" fontFamily="sans-serif" textAnchor="middle" letterSpacing="1">WEBHELP</text>
    </svg>
  ),
  "webhelp.com": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#1E1B4B" />
      <circle cx="100" cy="90" r="45" fill="none" stroke="#FBBF24" strokeWidth="10" />
      <path d="M 75 75 L 90 115 L 100 90 L 110 115 L 125 75" stroke="#FFFFFF" strokeWidth="10" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <text x="100" y="165" fill="#FBBF24" fontSize="18" fontWeight="800" fontFamily="sans-serif" textAnchor="middle" letterSpacing="1">WEBHELP</text>
    </svg>
  ),

  // ==================== VECTRUS ====================
  "vectrus": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#0A2540" />
      <path d="M 60 65 L 100 135 L 140 65" stroke="#00D2C4" strokeWidth="16" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <path d="M 80 65 L 100 100 L 120 65" stroke="#FFFFFF" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <text x="100" y="170" fill="#FFFFFF" fontSize="18" fontWeight="800" fontFamily="sans-serif" textAnchor="middle" letterSpacing="1.5">VECTRUS</text>
    </svg>
  ),
  "vectrus.com": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#0A2540" />
      <path d="M 60 65 L 100 135 L 140 65" stroke="#00D2C4" strokeWidth="16" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <path d="M 80 65 L 100 100 L 120 65" stroke="#FFFFFF" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <text x="100" y="170" fill="#FFFFFF" fontSize="18" fontWeight="800" fontFamily="sans-serif" textAnchor="middle" letterSpacing="1.5">VECTRUS</text>
    </svg>
  ),

  // ==================== INVENTEC ====================
  "inventec": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#0284C7" />
      <path d="M 60 55 H 140 M 100 55 V 145 M 60 145 H 140" stroke="#FFFFFF" strokeWidth="16" strokeLinecap="round" />
      <path d="M 85 80 L 100 65 L 115 80" stroke="#38BDF8" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <text x="100" y="175" fill="#FFFFFF" fontSize="16" fontWeight="900" fontFamily="sans-serif" textAnchor="middle" letterSpacing="1">INVENTEC</text>
    </svg>
  ),
  "inventec.com": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#0284C7" />
      <path d="M 60 55 H 140 M 100 55 V 145 M 60 145 H 140" stroke="#FFFFFF" strokeWidth="16" strokeLinecap="round" />
      <path d="M 85 80 L 100 65 L 115 80" stroke="#38BDF8" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <text x="100" y="175" fill="#FFFFFF" fontSize="16" fontWeight="900" fontFamily="sans-serif" textAnchor="middle" letterSpacing="1">INVENTEC</text>
    </svg>
  ),

  // ==================== NIPPON STEEL ====================
  "nippon steel": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#00205B" />
      <path d="M 50 120 L 100 45 L 150 120 Z" stroke="#E2E8F0" strokeWidth="12" strokeLinejoin="round" fill="none" />
      <path d="M 75 120 L 100 80 L 125 120 Z" stroke="#10B981" strokeWidth="8" strokeLinejoin="round" fill="none" />
      <circle cx="100" cy="115" r="14" fill="#EF4444" />
      <text x="100" y="168" fill="#FFFFFF" fontSize="16" fontWeight="900" fontFamily="sans-serif" textAnchor="middle" letterSpacing="1">NIPPON STEEL</text>
    </svg>
  ),
  "nipponsteel.com": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#00205B" />
      <path d="M 50 120 L 100 45 L 150 120 Z" stroke="#E2E8F0" strokeWidth="12" strokeLinejoin="round" fill="none" />
      <path d="M 75 120 L 100 80 L 125 120 Z" stroke="#10B981" strokeWidth="8" strokeLinejoin="round" fill="none" />
      <circle cx="100" cy="115" r="14" fill="#EF4444" />
      <text x="100" y="168" fill="#FFFFFF" fontSize="16" fontWeight="900" fontFamily="sans-serif" textAnchor="middle" letterSpacing="1">NIPPON STEEL</text>
    </svg>
  ),

  // ==================== KIMBALL ELECTRONICS ====================
  "kimball electronics": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#0F172A" />
      <path d="M 60 45 V 155 M 60 100 H 110 M 110 45 V 155" stroke="#00D2C4" strokeWidth="15" strokeLinecap="round" />
      <path d="M 110 100 L 150 55 M 110 100 L 150 145" stroke="#10B981" strokeWidth="15" strokeLinecap="round" />
      <circle cx="60" cy="45" r="8" fill="#FFFFFF" />
      <circle cx="110" cy="45" r="8" fill="#FFFFFF" />
      <circle cx="150" cy="55" r="8" fill="#FFFFFF" />
      <circle cx="155" cy="145" r="8" fill="#FFFFFF" />
      <circle cx="60" cy="155" r="8" fill="#FFFFFF" />
      <circle cx="110" cy="155" r="8" fill="#FFFFFF" />
      <text x="100" y="175" fill="#00D2C4" fontSize="14" fontWeight="800" fontFamily="sans-serif" textAnchor="middle" letterSpacing="1.5">KIMBALL</text>
    </svg>
  ),
  "kimballelectronics.com": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#0F172A" />
      <path d="M 60 45 V 155 M 60 100 H 110 M 110 45 V 155" stroke="#00D2C4" strokeWidth="15" strokeLinecap="round" />
      <path d="M 110 100 L 150 55 M 110 100 L 150 145" stroke="#10B981" strokeWidth="15" strokeLinecap="round" />
      <circle cx="60" cy="45" r="8" fill="#FFFFFF" />
      <circle cx="110" cy="45" r="8" fill="#FFFFFF" />
      <circle cx="150" cy="55" r="8" fill="#FFFFFF" />
      <circle cx="155" cy="145" r="8" fill="#FFFFFF" />
      <circle cx="60" cy="155" r="8" fill="#FFFFFF" />
      <circle cx="110" cy="155" r="8" fill="#FFFFFF" />
      <text x="100" y="175" fill="#00D2C4" fontSize="14" fontWeight="800" fontFamily="sans-serif" textAnchor="middle" letterSpacing="1.5">KIMBALL</text>
    </svg>
  ),

  // ==================== NIPPON LIGHT METAL ====================
  "nippon light metal": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#0B132B" />
      <polygon points="100,35 155,67 155,133 100,165 45,133 45,67" stroke="#38BDF8" strokeWidth="12" strokeLinejoin="round" fill="none" />
      <polygon points="100,55 137,77 137,123 100,145 63,123 63,77" stroke="#FFFFFF" strokeWidth="6" strokeLinejoin="round" fill="none" />
      <path d="M 100 35 V 165" stroke="#38BDF8" strokeWidth="6" strokeDasharray="10 12" />
      <text x="100" y="112" fill="#FFFFFF" fontSize="36" fontWeight="900" fontFamily="sans-serif" textAnchor="middle">NLM</text>
    </svg>
  ),
  "nlm.co.jp": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#0B132B" />
      <polygon points="100,35 155,67 155,133 100,165 45,133 45,67" stroke="#38BDF8" strokeWidth="12" strokeLinejoin="round" fill="none" />
      <polygon points="100,55 137,77 137,123 100,145 63,123 63,77" stroke="#FFFFFF" strokeWidth="6" strokeLinejoin="round" fill="none" />
      <path d="M 100 35 V 165" stroke="#38BDF8" strokeWidth="6" strokeDasharray="10 12" />
      <text x="100" y="112" fill="#FFFFFF" fontSize="36" fontWeight="900" fontFamily="sans-serif" textAnchor="middle">NLM</text>
    </svg>
  ),

  // ==================== NIPPON SHINYAKU ====================
  "nippon shinyaku": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#00695C" />
      <circle cx="100" cy="95" r="50" stroke="#00D2C4" strokeWidth="8" fill="none" />
      <rect x="86" y="65" width="28" height="60" rx="14" fill="#FFFFFF" transform="rotate(45 100 95)" />
      <circle cx="89" cy="84" r="7" fill="#00695C" />
      <circle cx="111" cy="106" r="7" fill="#00D2C4" />
      <text x="100" y="165" fill="#FFFFFF" fontSize="14" fontWeight="800" fontFamily="sans-serif" textAnchor="middle" letterSpacing="1">NIPPON SHINYAKU</text>
    </svg>
  ),
  "nippon-shinyaku.co.jp": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#00695C" />
      <circle cx="100" cy="95" r="50" stroke="#00D2C4" strokeWidth="8" fill="none" />
      <rect x="86" y="65" width="28" height="60" rx="14" fill="#FFFFFF" transform="rotate(45 100 95)" />
      <circle cx="89" cy="84" r="7" fill="#00695C" />
      <circle cx="111" cy="106" r="7" fill="#00D2C4" />
      <text x="100" y="165" fill="#FFFFFF" fontSize="14" fontWeight="800" fontFamily="sans-serif" textAnchor="middle" letterSpacing="1">NIPPON SHINYAKU</text>
    </svg>
  ),

  // ==================== MITSUBISHI UFJ (MUFG) ====================
  "mitsubishi ufj": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#FFFFFF" />
      <circle cx="100" cy="100" r="75" fill="#E60012" />
      <circle cx="100" cy="100" r="45" fill="none" stroke="#FFFFFF" strokeWidth="12" />
      <rect x="88" y="40" width="24" height="60" fill="#E60012" />
      <text x="100" y="172" fill="#FFFFFF" fontSize="20" fontWeight="900" fontFamily="sans-serif" textAnchor="middle" letterSpacing="1">MUFG</text>
    </svg>
  ),
  "mufg.jp": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#FFFFFF" />
      <circle cx="100" cy="100" r="75" fill="#E60012" />
      <circle cx="100" cy="100" r="45" fill="none" stroke="#FFFFFF" strokeWidth="12" />
      <rect x="88" y="40" width="24" height="60" fill="#E60012" />
      <text x="100" y="172" fill="#FFFFFF" fontSize="20" fontWeight="900" fontFamily="sans-serif" textAnchor="middle" letterSpacing="1">MUFG</text>
    </svg>
  ),

  // ==================== SUMITOMO MITSUI ====================
  "sumitomo mitsui": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#004D3F" />
      <path d="M 60 70 L 100 130 L 140 70" stroke="#81C784" strokeWidth="16" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <path d="M 100 50 L 100 130" stroke="#FFF176" strokeWidth="10" strokeLinecap="round" fill="none" />
      <text x="100" y="170" fill="#FFFFFF" fontSize="16" fontWeight="800" fontFamily="sans-serif" textAnchor="middle" letterSpacing="1">SMBC</text>
    </svg>
  ),
  "smfg.co.jp": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#004D3F" />
      <path d="M 60 70 L 100 130 L 140 70" stroke="#81C784" strokeWidth="16" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <path d="M 100 50 L 100 130" stroke="#FFF176" strokeWidth="10" strokeLinecap="round" fill="none" />
      <text x="100" y="170" fill="#FFFFFF" fontSize="16" fontWeight="800" fontFamily="sans-serif" textAnchor="middle" letterSpacing="1">SMBC</text>
    </svg>
  ),

  // ==================== MIZUHO ====================
  "mizuho": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#0C2074" />
      <path d="M 50 100 H 150" stroke="#E3120B" strokeWidth="12" strokeLinecap="round" />
      <path d="M 75 70 C 75 100, 125 100, 125 70" stroke="#FFFFFF" strokeWidth="8" strokeLinecap="round" fill="none" />
      <text x="100" y="150" fill="#FFFFFF" fontSize="16" fontWeight="bold" fontFamily="sans-serif" textAnchor="middle">MIZUHO</text>
    </svg>
  ),
  "mizuho-fg.co.jp": (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full p-2">
      <rect width="200" height="200" rx="40" fill="#0C2074" />
      <path d="M 50 100 H 150" stroke="#E3120B" strokeWidth="12" strokeLinecap="round" />
      <path d="M 75 70 C 75 100, 125 100, 125 70" stroke="#FFFFFF" strokeWidth="8" strokeLinecap="round" fill="none" />
      <text x="100" y="150" fill="#FFFFFF" fontSize="16" fontWeight="bold" fontFamily="sans-serif" textAnchor="middle">MIZUHO</text>
    </svg>
  ),

  // ==================== FICTIONAL EASTER EGG CORPORATIONS ====================
  "omni consumer products": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-stone-900">
      <path d="M100 25 L165 62 L165 138 L100 175 L35 138 L35 62 Z" fill="none" stroke="#D1D5DB" strokeWidth="8"/>
      <path d="M100 25 L100 175 M35 62 L165 138 M35 138 L165 62" stroke="#D1D5DB" strokeWidth="6" strokeDasharray="5 5"/>
      <rect x="65" y="75" width="70" height="50" rx="4" fill="#D1D5DB"/>
      <text x="100" y="110" fill="#111827" fontSize="24" fontWeight="900" textAnchor="middle" fontFamily="sans-serif">OCP</text>
    </svg>
  ),
  "weyland-yutani corp": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-stone-950">
      <path d="M 30 75 L 100 115 L 170 75 L 155 135 L 100 100 L 45 135 Z" fill="#F59E0B" />
      <path d="M 100 100 L 100 155" stroke="#FBBF24" strokeWidth="8" strokeLinecap="round" />
      <text x="100" y="172" fill="#F59E0B" fontSize="11" fontWeight="800" textAnchor="middle" letterSpacing="1">WEYLAND-YUTANI</text>
    </svg>
  ),
  "tyrell corporation": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#0A1128]">
      <polygon points="100,30 165,140 35,140" stroke="#38BDF8" strokeWidth="8" fill="none" />
      <circle cx="85" cy="100" r="12" fill="#38BDF8" />
      <circle cx="115" cy="100" r="12" fill="#38BDF8" />
      <polygon points="100,105 95,118 105,118" fill="#F59E0B" />
      <text x="100" y="170" fill="#38BDF8" fontSize="14" fontWeight="900" textAnchor="middle" letterSpacing="2">TYRELL</text>
    </svg>
  ),
  "umbrella pharmaceuticals": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-1.5 bg-stone-900">
      <circle cx="100" cy="100" r="85" fill="#FFFFFF" />
      <path d="M100 100 L160 100 A60 60 0 0 1 142.4 142.4 Z" fill="#DC2626" />
      <path d="M100 100 L100 160 A60 60 0 0 1 57.6 142.4 Z" fill="#DC2626" />
      <path d="M100 100 L40 100 A60 60 0 0 1 57.6 57.6 Z" fill="#DC2626" />
      <path d="M100 100 L100 40 A60 60 0 0 1 142.4 57.6 Z" fill="#DC2626" />
      <circle cx="100" cy="100" r="24" fill="#FFFFFF" stroke="#DC2626" strokeWidth="4" />
      <text x="100" y="185" fill="#FFFFFF" fontSize="12" fontWeight="900" textAnchor="middle" letterSpacing="1.5">UMBRELLA</text>
    </svg>
  ),
  "cyberdyne systems": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-slate-950">
      <polygon points="100,30 165,145 35,145" fill="none" stroke="#2563EB" strokeWidth="8"/>
      <polygon points="100,55 145,130 55,130" fill="#60A5FA" opacity="0.8"/>
      <ellipse cx="100" cy="105" rx="30" ry="10" fill="#FFFFFF" opacity="0.7"/>
      <text x="100" y="175" fill="#0284C7" fontSize="12" fontWeight="bold" textAnchor="middle" letterSpacing="1">CYBERDYNE</text>
    </svg>
  ),
  "ingen": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-stone-900">
      <circle cx="100" cy="100" r="85" fill="#EF4444" stroke="#F59E0B" strokeWidth="6" />
      <path d="M70 120 C70 90, 85 65, 120 65 C135 65, 140 75, 140 85 C140 100, 115 105, 115 110 C115 115, 130 115, 125 125 C115 135, 90 135, 80 130 C75 128, 70 125, 70 120 Z" fill="#111827" />
      <circle cx="120" cy="80" r="6" fill="#F59E0B" />
      <path d="M100 115 L108 120 M108 115 L116 120 M116 115 L124 120" stroke="#EF4444" strokeWidth="3" />
      <text x="100" y="172" fill="#FFFFFF" fontSize="18" fontWeight="900" textAnchor="middle" letterSpacing="1">InGen</text>
    </svg>
  ),
  "globex corporation": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-green-950">
      <circle cx="100" cy="100" r="75" fill="none" stroke="#22C55E" strokeWidth="8" />
      <ellipse cx="100" cy="100" rx="75" ry="25" fill="none" stroke="#22C55E" strokeWidth="4" />
      <ellipse cx="100" cy="100" rx="25" ry="75" fill="none" stroke="#22C55E" strokeWidth="4" />
      <line x1="25" y1="100" x2="175" y2="100" stroke="#22C55E" strokeWidth="4" />
      <line x1="100" y1="25" x2="100" y2="175" stroke="#22C55E" strokeWidth="4" />
      <text x="100" y="132" fill="#FFFFFF" fontSize="24" fontWeight="900" textAnchor="middle" letterSpacing="1">GLOBEX</text>
    </svg>
  ),
  "soylent corp": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#0F172A]">
      <rect x="35" y="35" width="130" height="130" rx="24" fill="#047857" />
      <path d="M70 120 Q100 60 130 60 Q100 110 70 120" fill="#34D399" />
      <circle cx="85" cy="75" r="8" fill="#A7F3D0" />
      <circle cx="115" cy="100" r="8" fill="#A7F3D0" />
      <text x="100" y="152" fill="#FFFFFF" fontSize="14" fontWeight="800" textAnchor="middle" letterSpacing="2">SOYLENT</text>
    </svg>
  ),
  "veridian dynamics": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-slate-100 border border-slate-300">
      <path d="M 50 50 L 100 140 L 150 50 L 125 50 L 100 100 L 75 50 Z" fill="#475569" />
      <circle cx="100" cy="120" r="10" fill="#0EA5E9" />
      <text x="100" y="172" fill="#1E293B" fontSize="13" fontWeight="900" textAnchor="middle" letterSpacing="0.5">VERIDIAN</text>
    </svg>
  ),
  "aperture science": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-white border border-stone-200">
      <circle cx="100" cy="95" r="72" fill="#111827" />
      <g stroke="#FFFFFF" strokeWidth="4">
        <path d="M 100 23 L 140 55 M 140 55 L 172 95 M 172 95 L 140 135 M 140 135 L 100 167 M 100 167 L 60 135 M 60 135 L 28 95 M 28 95 L 60 55 M 60 55 L 100 23" />
        <path d="M 60 55 L 100 55 M 100 23 L 100 95 M 140 55 L 110 95 M 172 95 L 110 120 M 140 135 L 90 120 M 100 167 L 80 100 M 60 135 L 60 80 M 28 95 L 75 75" />
      </g>
      <circle cx="100" cy="95" r="28" fill="#FFFFFF" />
      <text x="100" y="182" fill="#111827" fontSize="12" fontWeight="900" textAnchor="middle" letterSpacing="1">APERTURE</text>
    </svg>
  ),
  "black mesa": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#2D2D2D]">
      <circle cx="100" cy="95" r="70" fill="none" stroke="#F97316" strokeWidth="10" />
      <path d="M 50 125 L 85 80 L 105 100 L 125 70 L 150 125 Z" fill="#F97316" />
      <rect x="75" y="115" width="50" height="20" fill="#2D2D2D" transform="rotate(-30 100 115)" />
      <text x="100" y="180" fill="#F97316" fontSize="12" fontWeight="900" textAnchor="middle" letterSpacing="1.5">BLACK MESA</text>
    </svg>
  ),
  "abstergo industries": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#1A1A1A]">
      <path d="M 100 25 L 165 140 L 35 140 Z" fill="none" stroke="#EF4444" strokeWidth="12" strokeLinejoin="round" />
      <polygon points="100,55 140,125 60,125" fill="#1A1A1A" />
      <polygon points="100,25 100,140" stroke="#EF4444" strokeWidth="10" />
      <text x="100" y="172" fill="#FFFFFF" fontSize="14" fontWeight="bold" textAnchor="middle" letterSpacing="2">ABSTERGO</text>
    </svg>
  ),
  "vault-tec": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#004B87]">
      <circle cx="100" cy="95" r="55" fill="none" stroke="#FFD100" strokeWidth="10" />
      {Array.from({ length: 8 }).map((_, i) => (
        <rect key={i} x="90" y="30" width="20" height="15" fill="#FFD100" transform={`rotate(${i * 45} 100 95)`} />
      ))}
      <circle cx="100" cy="95" r="40" fill="#FFD100" />
      <circle cx="100" cy="95" r="24" fill="#004B87" />
      <text x="100" y="175" fill="#FFD100" fontSize="15" fontWeight="900" textAnchor="middle" letterSpacing="1">VAULT-TEC</text>
    </svg>
  ),
  "momcorp": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#7F1D1D]">
      <circle cx="100" cy="100" r="85" fill="#312E81" />
      <path d="M 100 45 C 70 45, 55 70, 55 100 Q 55 145, 100 160 C 145 145, 145 100, 145 100 C 145 70, 130 45, 100 45 Z" fill="#DC2626" />
      <circle cx="100" cy="100" r="28" fill="#FBBF24" />
      <text x="100" y="152" fill="#FFFFFF" fontSize="24" fontWeight="900" textAnchor="middle">MOM</text>
    </svg>
  ),
  "e corp": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#1E3A8A]">
      <circle cx="100" cy="100" r="85" fill="#FFFFFF" />
      <text x="110" y="125" fill="#DC2626" fontSize="120" fontWeight="900" fontFamily="serif" textAnchor="middle" transform="skewX(-15)">e</text>
      <rect x="35" y="102" width="60" height="15" fill="#DC2626" transform="skewX(-15) rotate(-10)" />
    </svg>
  ),
  "hooli": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#001B0E] rounded-md">
      <circle cx="100" cy="100" r="80" fill="none" stroke="#10B981" strokeWidth="6" opacity="0.4" />
      <circle cx="100" cy="100" r="60" fill="none" stroke="#059669" strokeWidth="4" strokeDasharray="188 50" />
      <path d="M 100 65 L 100 135 M 100 95 C 120 95, 130 110, 130 135" stroke="#10B981" strokeWidth="15" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <circle cx="130" cy="135" r="7.5" fill="#34D399" />
      <circle cx="100" cy="65" r="7.5" fill="#34D399" />
      <circle cx="100" cy="135" r="7.5" fill="#34D399" />
      <text x="100" y="172" fill="#10B981" fontSize="16" fontWeight="900" textAnchor="middle" letterSpacing="1">hooli</text>
    </svg>
  ),
  "hooli.com": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#001B0E] rounded-md">
      <circle cx="100" cy="100" r="80" fill="none" stroke="#10B981" strokeWidth="6" opacity="0.4" />
      <circle cx="100" cy="100" r="60" fill="none" stroke="#059669" strokeWidth="4" strokeDasharray="188 50" />
      <path d="M 100 65 L 100 135 M 100 95 C 120 95, 130 110, 130 135" stroke="#10B981" strokeWidth="15" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <circle cx="130" cy="135" r="7.5" fill="#34D399" />
      <circle cx="100" cy="65" r="7.5" fill="#34D399" />
      <circle cx="100" cy="135" r="7.5" fill="#34D399" />
      <text x="100" y="172" fill="#10B981" fontSize="16" fontWeight="900" textAnchor="middle" letterSpacing="1">hooli</text>
    </svg>
  ),
  "sterling cooper": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#0C1E26] rounded-md">
      <defs>
        <linearGradient id="sc-gold-sc" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#C87D55" />
          <stop offset="100%" stopColor="#8C4A30" />
        </linearGradient>
      </defs>
      <rect x="25" y="25" width="150" height="150" fill="none" stroke="url(#sc-gold-sc)" strokeWidth="4" />
      <rect x="35" y="35" width="130" height="130" fill="none" stroke="url(#sc-gold-sc)" strokeWidth="1.5" opacity="0.5" />
      <text x="100" y="112" fill="#FFFFFF" fontSize="64" fontWeight="300" fontFamily="Georgia, serif" textAnchor="middle" letterSpacing="1">SC</text>
      <text x="100" y="148" fill="url(#sc-gold-sc)" fontSize="10" fontWeight="700" fontFamily="sans-serif" textAnchor="middle" letterSpacing="3">STERLING COOPER</text>
    </svg>
  ),
  "sterlingcooper.com": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#0C1E26] rounded-md">
      <defs>
        <linearGradient id="sc-gold-scc" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#C87D55" />
          <stop offset="100%" stopColor="#8C4A30" />
        </linearGradient>
      </defs>
      <rect x="25" y="25" width="150" height="150" fill="none" stroke="url(#sc-gold-scc)" strokeWidth="4" />
      <rect x="35" y="35" width="130" height="130" fill="none" stroke="url(#sc-gold-scc)" strokeWidth="1.5" opacity="0.5" />
      <text x="100" y="112" fill="#FFFFFF" fontSize="64" fontWeight="300" fontFamily="Georgia, serif" textAnchor="middle" letterSpacing="1">SC</text>
      <text x="100" y="148" fill="url(#sc-gold-scc)" fontSize="10" fontWeight="700" fontFamily="sans-serif" textAnchor="middle" letterSpacing="3">STERLING COOPER</text>
    </svg>
  ),
  "globochem": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#1A0B2E] rounded-md">
      <defs>
        <linearGradient id="toxic-glow-gc" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#A7F3D0" />
          <stop offset="100%" stopColor="#059669" />
        </linearGradient>
      </defs>
      <circle cx="100" cy="100" r="80" fill="none" stroke="#A7F3D0" strokeWidth="2" strokeDasharray="5 5" opacity="0.3" />
      <path d="M 85 60 L 85 75 C 70 85, 60 100, 60 120 C 60 142, 78 160, 100 160 C 122 160, 140 142, 140 120 C 140 100, 130 85, 115 75 L 115 60 Z" fill="none" stroke="url(#toxic-glow-gc)" strokeWidth="10" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M 80 55 H 120" stroke="url(#toxic-glow-gc)" strokeWidth="12" strokeLinecap="round" />
      <circle cx="100" cy="115" r="8" fill="#10B981" />
      <circle cx="85" cy="130" r="12" fill="#10B981" />
      <circle cx="115" cy="125" r="10" fill="#10B981" />
      <circle cx="95" cy="90" r="5" fill="#34D399" />
      <circle cx="108" cy="100" r="3" fill="#34D399" />
      <text x="100" y="180" fill="#A7F3D0" fontSize="13" fontWeight="900" textAnchor="middle" letterSpacing="2">GLOBOCHEM</text>
    </svg>
  ),
  "globochem.com": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#1A0B2E] rounded-md">
      <defs>
        <linearGradient id="toxic-glow-gcc" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#A7F3D0" />
          <stop offset="100%" stopColor="#059669" />
        </linearGradient>
      </defs>
      <circle cx="100" cy="100" r="80" fill="none" stroke="#A7F3D0" strokeWidth="2" strokeDasharray="5 5" opacity="0.3" />
      <path d="M 85 60 L 85 75 C 70 85, 60 100, 60 120 C 60 142, 78 160, 100 160 C 122 160, 140 142, 140 120 C 140 100, 130 85, 115 75 L 115 60 Z" fill="none" stroke="url(#toxic-glow-gcc)" strokeWidth="10" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M 80 55 H 120" stroke="url(#toxic-glow-gcc)" strokeWidth="12" strokeLinecap="round" />
      <circle cx="100" cy="115" r="8" fill="#10B981" />
      <circle cx="85" cy="130" r="12" fill="#10B981" />
      <circle cx="115" cy="125" r="10" fill="#10B981" />
      <circle cx="95" cy="90" r="5" fill="#34D399" />
      <circle cx="108" cy="100" r="3" fill="#34D399" />
      <text x="100" y="180" fill="#A7F3D0" fontSize="13" fontWeight="900" textAnchor="middle" letterSpacing="2">GLOBOCHEM</text>
    </svg>
  ),
  "hanso foundation": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-stone-900">
      <polygon points="100,20 155,43 180,100 155,157 100,180 45,157 20,100 45,43" stroke="#FFFFFF" strokeWidth="10" strokeLinejoin="miter" fill="none" />
      <circle cx="100" cy="100" r="50" fill="none" stroke="#FFFFFF" strokeWidth="15" strokeDasharray="15 8" />
      <circle cx="100" cy="100" r="20" fill="#FFFFFF" />
      <text x="100" y="105" fill="#111827" fontSize="14" fontWeight="900" textAnchor="middle">H</text>
    </svg>
  ),
  "massive dynamic": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#1E1B4B]">
      <path d="M 45 135 V 65 L 100 110 L 155 65 V 135" stroke="#A78BFA" strokeWidth="16" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <path d="M 100 110 V 150" stroke="#F472B6" strokeWidth="10" strokeLinecap="round" />
      <text x="100" y="172" fill="#A78BFA" fontSize="10" fontWeight="bold" textAnchor="middle" letterSpacing="1">MASSIVE DYNAMIC</text>
    </svg>
  ),
  "vought international": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-slate-900">
      <path d="M40 50 L100 135 L160 50 L135 50 L100 100 L65 50 Z" fill="#DC2626" />
      <path d="M 40 60 L 100 145 L 160 60 L 135 60 L 100 110 L 65 60 Z" fill="#1E40AF" opacity="0.6" />
      <text x="100" y="175" fill="#FFFFFF" fontSize="18" fontWeight="900" textAnchor="middle" letterSpacing="2">VOUGHT</text>
    </svg>
  ),
  "roxxon energy": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#7F1D1D]">
      <circle cx="100" cy="100" r="80" stroke="#FFD100" strokeWidth="8" fill="none" />
      <text x="100" y="132" fill="#FFFFFF" fontSize="88" fontWeight="900" textAnchor="middle" letterSpacing="-2">R</text>
    </svg>
  ),
  "stark industries (legacy)": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#0F172A]">
      <path d="M30 85 L170 85 L140 115 L60 115 Z" fill="#38BDF8" opacity="0.3" />
      <line x1="30" y1="85" x2="170" y2="85" stroke="#38BDF8" strokeWidth="8" strokeLinecap="round" />
      <text x="100" y="130" fill="#FFFFFF" fontSize="28" fontWeight="900" fontStyle="italic" textAnchor="middle" letterSpacing="1">STARK</text>
      <text x="100" y="152" fill="#38BDF8" fontSize="12" fontWeight="bold" textAnchor="middle" letterSpacing="3">INDUSTRIES</text>
    </svg>
  ),
  "oscorp": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-emerald-950">
      <circle cx="100" cy="95" r="60" stroke="#10B981" strokeWidth="12" fill="none" />
      <ellipse cx="100" cy="95" rx="60" ry="20" stroke="#34D399" strokeWidth="4" transform="rotate(45 100 95)" fill="none" />
      <text x="100" y="172" fill="#34D399" fontSize="20" fontWeight="900" textAnchor="middle" letterSpacing="2">OSCORP</text>
    </svg>
  ),
  "lexcorp": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-stone-900">
      <path d="M 60 40 V 140 H 140 V 110 H 90 V 40 Z" fill="#DC2626" />
      <text x="100" y="172" fill="#FFFFFF" fontSize="22" fontWeight="900" textAnchor="middle">LEXCORP</text>
    </svg>
  ),
  "wayne enterprises (legacy)": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-stone-950">
      <rect x="25" y="25" width="150" height="150" stroke="#D97706" strokeWidth="4" fill="none" />
      <text x="100" y="112" fill="#F59E0B" fontSize="80" fontFamily="serif" fontWeight="900" textAnchor="middle">W</text>
      <text x="100" y="150" fill="#F1F5F9" fontSize="11" fontWeight="800" textAnchor="middle" letterSpacing="3">WAYNE ENT.</text>
    </svg>
  ),
  "shinra electric power": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-stone-900">
      <polygon points="100,25 160,85 100,145 40,85" stroke="#EF4444" strokeWidth="8" fill="none" />
      <circle cx="100" cy="85" r="24" fill="#EF4444" />
      <text x="100" y="172" fill="#EF4444" fontSize="20" fontWeight="bold" textAnchor="middle" letterSpacing="2">SHINRA</text>
    </svg>
  ),
  "mishima zaibatsu": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-stone-950">
      <path d="M 100 30 L 160 130 H 40 Z" stroke="#EF4444" strokeWidth="8" fill="none" />
      <circle cx="100" cy="95" r="24" fill="#F59E0B" />
      <text x="100" y="172" fill="#F59E0B" fontSize="16" fontWeight="bold" textAnchor="middle">MISHIMA</text>
    </svg>
  ),
  "g-corp": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-cyan-950">
      <path d="M 145 100 C 145 60, 110 40, 100 40 C 60 40, 45 75, 45 100 C 45 125, 60 160, 100 160 C 130 160, 145 140, 145 120 H 100 V 95 H 145" stroke="#22D3EE" strokeWidth="12" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  ),
  "arasaka corporation": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-stone-900">
      <circle cx="100" cy="85" r="30" stroke="#F59E0B" strokeWidth="8" fill="none" />
      <line x1="100" y1="85" x2="100" y2="145" stroke="#F59E0B" strokeWidth="12" strokeLinecap="round" />
      <path d="M 65 115 Q 100 115 100 85" stroke="#F59E0B" strokeWidth="12" fill="none" strokeLinecap="round" />
      <path d="M 135 115 Q 100 115 100 85" stroke="#F59E0B" strokeWidth="12" fill="none" strokeLinecap="round" />
      <text x="100" y="175" fill="#FFFFFF" fontSize="16" fontWeight="bold" textAnchor="middle" letterSpacing="2">ARASAKA</text>
    </svg>
  ),
  "militech": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-red-950">
      <rect x="40" y="40" width="120" height="120" rx="12" stroke="#EF4444" strokeWidth="8" fill="none" />
      <path d="M 60 70 L 100 110 L 140 70" stroke="#EF4444" strokeWidth="12" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <path d="M 60 100 L 100 140 L 140 100" stroke="#FFFFFF" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <text x="100" y="172" fill="#FFFFFF" fontSize="12" fontWeight="bold" textAnchor="middle" letterSpacing="1">MILITECH</text>
    </svg>
  ),
  "kang tao": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#1E1B4B]">
      <polygon points="100,30 160,65 160,135 100,170 40,135 40,65" stroke="#F59E0B" strokeWidth="8" fill="none" strokeLinejoin="round" />
      <polygon points="100,55 140,80 140,120 100,145 60,120 60,80" fill="#F59E0B" opacity="0.4" />
      <text x="100" y="110" fill="#FFFFFF" fontSize="18" fontWeight="bold" textAnchor="middle">KANG</text>
    </svg>
  ),
  "biotechnica": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-teal-950">
      <circle cx="100" cy="100" r="80" stroke="#059669" strokeWidth="6" fill="none" />
      <path d="M80 140 Q100 80 120 140" fill="none" stroke="#34D399" strokeWidth="8" strokeLinecap="round" />
      <path d="M120 60 Q100 120 80 60" fill="none" stroke="#34D399" strokeWidth="8" strokeLinecap="round" />
      <path d="M 70 100 C 130 70, 70 70, 130 100" stroke="#F59E0B" strokeWidth="4" fill="none" />
      <text x="100" y="172" fill="#FFFFFF" fontSize="10" fontWeight="bold" textAnchor="middle" letterSpacing="1.5">BIOTECHNICA</text>
    </svg>
  ),
  "petrochem": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-stone-900">
      <path d="M60 45 L140 45 L120 135 L80 135 Z" fill="#FBBF24" />
      <path d="M75 45 C75 100, 125 100, 125 45" stroke="#DC2626" strokeWidth="8" fill="none" />
      <text x="100" y="165" fill="#FBBF24" fontSize="18" fontWeight="bold" textAnchor="middle">PETROCHEM</text>
    </svg>
  ),
  "night corp": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#030712]">
      <path d="M40 70 C40 40, 160 40, 160 70 Q160 130, 100 165 C40 130, 40 70, 40 70 Z" fill="none" stroke="#8B5CF6" strokeWidth="8" />
      <circle cx="100" cy="85" r="28" fill="#3B82F6" opacity="0.6" />
      <circle cx="120" cy="75" r="28" fill="#030712" />
      <text x="100" y="152" fill="#8B5CF6" fontSize="12" fontWeight="bold" textAnchor="middle">NIGHT CORP</text>
    </svg>
  ),
  "trauma team international": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-stone-900">
      <circle cx="100" cy="95" r="65" fill="#FBBF24" />
      <path d="M 85 95 H 115 M 100 80 V 110" stroke="#047857" strokeWidth="18" strokeLinecap="square" />
      <path d="M 40 100 L 70 120 L 60 70 Z" fill="#DC2626" />
      <path d="M 160 100 L 130 120 L 140 70 Z" fill="#DC2626" />
      <text x="100" y="175" fill="#FBBF24" fontSize="13" fontWeight="900" textAnchor="middle">TRAUMA TEAM</text>
    </svg>
  ),
  "delamain": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#0B0F19]">
      <path d="M100 30 C138 30, 165 57, 165 95 C165 133, 138 160, 100 160 H 55 V 30 H 100 Z" stroke="#38BDF8" strokeWidth="12" strokeLinejoin="round" fill="none" />
      <circle cx="95" cy="95" r="24" fill="#38BDF8" />
      <text x="100" y="180" fill="#FFFFFF" fontSize="14" fontWeight="bold" textAnchor="middle" letterSpacing="2">DELAMAIN</text>
    </svg>
  ),
  "weyland-yutani": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-stone-950">
      <path d="M 30 75 L 100 115 L 170 75 L 155 135 L 100 100 L 45 135 Z" fill="#F59E0B" />
      <path d="M 100 100 L 100 155" stroke="#FBBF24" strokeWidth="8" strokeLinecap="round" />
      <text x="100" y="172" fill="#F59E0B" fontSize="11" fontWeight="800" textAnchor="middle" letterSpacing="1">WEYLAND-YUTANI</text>
    </svg>
  ),
  "umbrella corporation": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-1.5 bg-stone-900">
      <circle cx="100" cy="100" r="85" fill="#FFFFFF" />
      <path d="M100 100 L160 100 A60 60 0 0 1 142.4 142.4 Z" fill="#DC2626" />
      <path d="M100 100 L100 160 A60 60 0 0 1 57.6 142.4 Z" fill="#DC2626" />
      <path d="M100 100 L40 100 A60 60 0 0 1 57.6 57.6 Z" fill="#DC2626" />
      <path d="M100 100 L100 40 A60 60 0 0 1 142.4 57.6 Z" fill="#DC2626" />
      <circle cx="100" cy="100" r="24" fill="#FFFFFF" stroke="#DC2626" strokeWidth="4" />
      <text x="100" y="185" fill="#FFFFFF" fontSize="12" fontWeight="900" textAnchor="middle" letterSpacing="1.5">UMBRELLA</text>
    </svg>
  ),
  "soylent": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#064E3B]">
      <circle cx="100" cy="100" r="85" fill="none" stroke="#EF4444" strokeWidth="6" />
      <path d="M100 35 C100 35, 120 60, 120 80 C120 91, 111 100, 100 100 C89 100, 80 91, 80 80 C80 60, 100 35, 100 35 Z" fill="#EAB308" />
      <path d="M60 120 Q100 100 140 120" stroke="#10B981" strokeWidth="8" strokeLinecap="round" fill="none" />
      <text x="100" y="172" fill="#EAB308" fontSize="12" fontWeight="900" textAnchor="middle" letterSpacing="1">SOYLENT GREEN</text>
    </svg>
  ),
  "aperture": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-white ring-1 ring-stone-200">
      <path d="M100 30 A 70 70 0 1 0 170 100 A 70 70 0 0 0 100 30" fill="none" stroke="#111827" strokeWidth="10"/>
      <path d="M50 82 L110 40 M110 40 L150 90 M150 90 L115 155 M115 155 L60 140 M60 140 L45 88 M45 88 L100 115" stroke="#111827" strokeWidth="6" strokeLinecap="round"/>
      <text x="100" y="182" fill="#111827" fontSize="12" fontWeight="900" textAnchor="middle" letterSpacing="1">APERTURE</text>
    </svg>
  ),
  "abstergo": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#1E293B]">
      <polygon points="100,25 165,138 35,138" stroke="#FFFFFF" strokeWidth="12" fill="none" />
      <polygon points="100,55 145,133 55,133" fill="#64748B" />
      <circle cx="100" cy="110" r="16" fill="#0F172A" />
      <text x="100" y="172" fill="#FFFFFF" fontSize="14" fontWeight="bold" textAnchor="middle" letterSpacing="2">ABSTERGO</text>
    </svg>
  ),
  "global resource extraction": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#1C1917]">
      <path d="M100 25 L170 65 L170 145 L100 185 L30 145 L30 65 Z" fill="none" stroke="#E7E5E4" strokeWidth="8" />
      <path d="M60 85 L140 135 M140 85 L60 135" stroke="#E7E5E4" strokeWidth="6" />
      <circle cx="100" cy="110" r="30" fill="#EA580C" />
      <polygon points="100,90 115,120 85,120" fill="#E7E5E4" />
      <text x="100" y="172" fill="#E7E5E4" fontSize="10" fontWeight="900" textAnchor="middle" letterSpacing="1">G.R.E.</text>
    </svg>
  ),
  "deep sea drilling ltd": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#082F49]">
      <path d="M100 35 L100 130 S100 160 80 165 S120 165 100 130 Z" fill="#F97316" />
      <rect x="75" y="145" width="50" height="15" rx="3" fill="#38BDF8" />
      <path d="M30 130 L170 130" stroke="#0284C7" strokeWidth="8" strokeLinecap="round" />
      <path d="M50 130 L100 50 L150 130" stroke="#E2E8F0" strokeWidth="6" strokeLinejoin="round" fill="none" />
      <text x="100" y="180" fill="#38BDF8" fontSize="10" fontWeight="800" textAnchor="middle" letterSpacing="1.5">DEEP SEA DRILL</text>
    </svg>
  ),
  "arctic oil consortium": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-1.5 bg-[#0F172A]">
      <path d="M100 30 L155 125 H45 Z" fill="none" stroke="#38BDF8" strokeWidth="8" strokeLinejoin="round" />
      <path d="M100 80 L135 140 H65 Z" fill="#E2E8F0" opacity="0.9" />
      <path d="M100 65 C100 65 115 90 115 105 C115 113 108 120 100 120 C92 120 85 113 85 105 C85 90 100 65 100 65 Z" fill="#EA580C" />
      <text x="100" y="175" fill="#38BDF8" fontSize="10" fontWeight="900" textAnchor="middle" letterSpacing="1">ARCTIC OIL</text>
    </svg>
  ),
  "amazonia deforestation corp": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-1.5 bg-[#14532D]">
      <circle cx="100" cy="100" r="80" fill="none" stroke="#F87171" strokeWidth="6" />
      <path d="M100 40 L140 105 H60 Z" fill="#047857" />
      <path d="M100 105 L150 145 H50 Z" fill="#059669" />
      <path d="M45 100 L155 100 M60 120 L140 120 M75 80 L125 80" stroke="#EF4444" strokeWidth="8" strokeLinecap="round" />
      <text x="100" y="175" fill="#F87171" fontSize="11" fontWeight="900" textAnchor="middle">AMAZONIA</text>
    </svg>
  ),
  "pacific plastic industries": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-1.5 bg-[#0D9488]">
      <path d="M30 100 C50 80, 80 120, 110 100 C140 80, 150 120, 170 100" stroke="#CCFBF1" strokeWidth="12" strokeLinecap="round" fill="none" />
      <path d="M30 130 C50 110, 80 150, 110 130 C140 110, 150 150, 170 130" stroke="#2DD4BF" strokeWidth="12" strokeLinecap="round" fill="none" />
      <rect x="85" y="60" width="30" height="60" rx="6" fill="#F3F4F6" stroke="#9CA3AF" strokeWidth="4" transform="rotate(30 100 90)" />
      <line x1="60" y1="50" x2="140" y2="130" stroke="#EF4444" strokeWidth="8" strokeLinecap="round" />
      <text x="100" y="172" fill="#CCFBF1" fontSize="11" fontWeight="900" textAnchor="middle">PACIFIC PLASTIC</text>
    </svg>
  ),
  "global coal & power": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-1.5 bg-[#171717]">
      <rect x="40" y="40" width="55" height="100" fill="#262626" stroke="#525252" strokeWidth="4" />
      <rect x="105" y="40" width="55" height="100" fill="#262626" stroke="#525252" strokeWidth="4" />
      <path d="M40 40 L67 15 M67 15 L95 40 M105 40 L132 15 M132 15 L160 40" stroke="#F59E0B" strokeWidth="6" strokeLinecap="round" />
      <circle cx="100" cy="115" r="35" fill="none" stroke="#F5BB1B" strokeWidth="6" />
      <path d="M90 115 H110 M100 105 V125" stroke="#FBBF24" strokeWidth="6" strokeLinecap="round" />
      <text x="100" y="175" fill="#F59E0B" fontSize="11" fontWeight="bold" textAnchor="middle">G_COAL_POWER</text>
    </svg>
  ),
  "chemical spill solutions": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-1.5 bg-[#451A03]">
      <polygon points="100,30 175,150 25,150" fill="#FBBF24" stroke="#78350F" strokeWidth="8" />
      <circle cx="100" cy="115" r="16" fill="#18181B" />
      <circle cx="78" cy="122" r="12" fill="#18181B" />
      <circle cx="122" cy="122" r="12" fill="#18181B" />
      <path d="M100 85 C95 85, 90 95, 100 115 C110 95, 105 85, 100 85 Z" fill="#A3E635" />
      <text x="100" y="180" fill="#FBBF24" fontSize="10" fontWeight="900" textAnchor="middle">TOXIC SPILL</text>
    </svg>
  ),
  "toxic waste management": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#022C22]">
      <rect x="60" y="45" width="80" height="95" rx="10" fill="#047857" stroke="#10B981" strokeWidth="6"/>
      <line x1="60" y1="75" x2="140" y2="75" stroke="#10B981" strokeWidth="6" />
      <line x1="60" y1="110" x2="140" y2="110" stroke="#10B981" strokeWidth="6" />
      <path d="M100 83 C92 83, 86 89, 86 97 C86 105, 92 111, 100 111 C108 111, 114 105, 114 97 C114 89, 108 83, 100 83 Z" fill="#84CC16" />
      <text x="100" y="172" fill="#10B981" fontSize="11" fontWeight="800" textAnchor="middle">HAZARD WASTE</text>
    </svg>
  ),
  "rainforest logging group": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-1.5 bg-[#451A03]">
      <circle cx="100" cy="90" r="60" fill="none" stroke="#D97706" strokeWidth="8" />
      <line x1="40" y1="90" x2="160" y2="90" stroke="#D97706" strokeWidth="4" />
      <line x1="100" y1="30" x2="100" y2="150" stroke="#D97706" strokeWidth="4" />
      <polygon points="65,130 100,55 135,130" fill="#065F46" />
      <path d="M50 110 L150 110" stroke="#EF4444" strokeWidth="10" strokeLinecap="round" />
      <text x="100" y="175" fill="#FBBF24" fontSize="11" fontWeight="900" textAnchor="middle">FOREST LOGGING</text>
    </svg>
  ),
  "rare earth ravagers": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#090D16]">
      <polygon points="100,30 160,70 160,135 100,175 40,135 40,70" fill="none" stroke="#F59E0B" strokeWidth="6" />
      <polygon points="100,60 140,90 140,135 100,165 60,135 60,90" fill="#1E3A8A" stroke="#3B82F6" strokeWidth="4" />
      <circle cx="100" cy="110" r="15" fill="#EC4899" />
      <text x="100" y="185" fill="#FBBF24" fontSize="10" fontWeight="900" textAnchor="middle">RARE EARTHS</text>
    </svg>
  ),
  "ocean trawler fleet": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#0C4A6E]">
      <path d="M30 130 C70 120, 130 120, 170 130 L160 160 H40 Z" fill="#4B5563" />
      <rect x="75" y="80" width="50" height="50" fill="#D1D5DB" />
      <line x1="40" y1="125" x2="40" y2="55" stroke="#E5E7EB" strokeWidth="6" />
      <line x1="160" y1="125" x2="160" y2="55" stroke="#E5E7EB" strokeWidth="6" />
      <path d="M40 70 Q100 130 160 70" stroke="#EF4444" strokeWidth="6" strokeLinecap="round" fill="none" />
      <text x="100" y="180" fill="#38BDF8" fontSize="11" fontWeight="900" textAnchor="middle">TRAWLER FLEETS</text>
    </svg>
  ),
  "mega-farm monopolies": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#064E3B]">
      <circle cx="100" cy="100" r="80" fill="none" stroke="#EAB308" strokeWidth="6" />
      <path d="M40 140 H160" stroke="#78350F" strokeWidth="8" />
      <line x1="60" y1="140" x2="60" y2="70" stroke="#78350F" strokeWidth="6" />
      <line x1="140" y1="140" x2="140" y2="70" stroke="#78350F" strokeWidth="6" />
      <path d="M80 60 C80 60, 90 90, 100 65 C110 90, 120 60, 120 60" fill="none" stroke="#EAB308" strokeWidth="6" strokeLinecap="round" />
      <text x="100" y="172" fill="#EAB308" fontSize="11" fontWeight="bold" textAnchor="middle">MEGA-FARM</text>
    </svg>
  ),
  "global pesticide partners": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#3F1B0B]">
      <circle cx="100" cy="100" r="75" fill="none" stroke="#A855F7" strokeWidth="6" />
      <path d="M70 100 L100 65 L130 100 L100 135 Z" fill="#84CC16" />
      <circle cx="100" cy="65" r="10" fill="#EF4444" />
      <circle cx="70" cy="100" r="10" fill="#EF4444" />
      <circle cx="130" cy="100" r="10" fill="#EF4444" />
      <circle cx="100" cy="135" r="10" fill="#EF4444" />
      <text x="100" y="175" fill="#FFFFFF" fontSize="10" fontWeight="900" textAnchor="middle">PESTICIDES</text>
    </svg>
  ),
  "fracking frontiers": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#1C1D1F]">
      <path d="M30 150 L100 60 L170 150" fill="none" stroke="#F97316" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M100 60 L100 170" stroke="#EA580C" strokeWidth="6" />
      <path d="M50 170 Q100 120 150 170" stroke="#38BDF8" strokeWidth="6" fill="none" />
      <text x="100" y="185" fill="#F97316" fontSize="11" fontWeight="bold" textAnchor="middle">FRACKING CO</text>
    </svg>
  ),
  "strip mine specialists": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#292524]">
      <path d="M30 60 H170 L150 100 H50 L30 60 Z" fill="#78716C" />
      <path d="M50 100 H150 L130 140 H70 L50 100 Z" fill="#44403C" />
      <path d="M70 140 H130 L115 170 H85 L70 140 Z" fill="#1C1917" stroke="#EF4444" strokeWidth="3" />
      <text x="100" y="45" fill="#E7E5E4" fontSize="11" fontWeight="900" textAnchor="middle">STRIP MINING</text>
    </svg>
  ),
  "dirty diesel logistics": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#0F172A]">
      <rect x="50" y="70" width="100" height="60" rx="4" fill="#334155" />
      <circle cx="70" cy="130" r="18" fill="#1E293B" stroke="#475569" strokeWidth="4" />
      <circle cx="130" cy="130" r="18" fill="#1E293B" stroke="#475569" strokeWidth="4" />
      <path d="M150 85 H170 V105 H150 Z" fill="#64748B" />
      <path d="M165 65 C165 65 180 50 180 35" stroke="#94A3B8" strokeWidth="8" strokeLinecap="round" />
      <circle cx="180" cy="25" r="10" fill="#EF4444" />
      <text x="100" y="165" fill="#94A3B8" fontSize="10" fontWeight="900" textAnchor="middle">DIRTY DIESEL</text>
    </svg>
  ),
  "smog city automotive": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#27272A]">
      <circle cx="100" cy="100" r="75" fill="none" stroke="#DC2626" strokeWidth="8" />
      <path d="M60 120 H140 C140 100, 130 90, 110 90 H90 C70 90, 60 100, 60 120 Z" fill="#71717A" />
      <circle cx="80" cy="120" r="12" fill="#09090B" />
      <circle cx="120" cy="120" r="12" fill="#09090B" />
      <path d="M140 105 C160 105, 175 90, 175 75 C175 60, 160 65, 140 70 Z" fill="#E4E4E7" opacity="0.8" />
      <text x="100" y="172" fill="#F4F4F5" fontSize="11" fontWeight="bold" textAnchor="middle">SMOG CITY AUTO</text>
    </svg>
  ),
  "landfill logistics": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#1C1917]">
      <polygon points="100,35 155,75 140,145 60,145 45,75" fill="#3F3F46" stroke="#71717A" strokeWidth="6" />
      <line x1="50" y1="75" x2="150" y2="75" stroke="#18181B" strokeWidth="4" />
      <line x1="55" y1="110" x2="145" y2="110" stroke="#18181B" strokeWidth="4" />
      <circle cx="100" cy="95" r="12" fill="#7C2D12" />
      <circle cx="85" cy="125" r="10" fill="#7C2D12" />
      <circle cx="115" cy="125" r="10" fill="#7C2D12" />
      <text x="100" y="175" fill="#D6D3D1" fontSize="11" fontWeight="950" textAnchor="middle">LANDFILL ENG</text>
    </svg>
  ),
  "e-waste exporters": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#090514]">
      <rect x="45" y="45" width="110" height="80" rx="8" fill="#1F2937" stroke="#DC2626" strokeWidth="6" />
      <polygon points="85,125 115,125 125,150 75,150" fill="#4B5563" />
      <line x1="55" y1="55" x2="145" y2="115" stroke="#EF4444" strokeWidth="8" strokeLinecap="round" />
      <path d="M100 65 L115 85 H85 Z" fill="#10B981" />
      <text x="100" y="180" fill="#E5E7EB" fontSize="11" fontWeight="bold" textAnchor="middle">E-WASTE EXPORTS</text>
    </svg>
  ),
  "blood diamond trading": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#111111]">
      <polygon points="100,25 155,75 100,165 45,75" fill="none" stroke="#DC2626" strokeWidth="8" strokeLinejoin="round" />
      <polygon points="100,45 135,75 100,140 65,75" fill="#334155" stroke="#FFFFFF" strokeWidth="2" />
      <path d="M100 25 L100 165 M45 75 H155" stroke="#DC2626" strokeWidth="3" />
      <circle cx="100" cy="115" r="15" fill="#DC2626" />
      <text x="100" y="182" fill="#E5E7EB" fontSize="10" fontWeight="900" textAnchor="middle" letterSpacing="1">BLOOD DIAMOND</text>
    </svg>
  ),
  "ivory coast cocoa cartel": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-1.5 bg-[#27160C]">
      <path d="M100 35 C140 35, 160 70, 160 100 C160 135, 130 165, 100 165 C70 165, 40 135, 40 100 C40 70, 60 35, 100 35 Z" fill="none" stroke="#EF4444" strokeWidth="8" />
      <polygon points="100,50 145,100 100,150 55,100" fill="#FBBF24" />
      <circle cx="100" cy="100" r="15" fill="#27160C" />
      <text x="100" y="181" fill="#FBBF24" fontSize="10" fontWeight="900" textAnchor="middle" letterSpacing="1">COCOA CARTEL</text>
    </svg>
  ),
  "mekong dam developers": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#0F172A]">
      <path d="M30 140 H170 L150 60 H50 Z" fill="#475569" stroke="#94A3B8" strokeWidth="6" />
      <line x1="50" y1="100" x2="150" y2="100" stroke="#334155" strokeWidth="6" />
      <path d="M100 60 L100 140 M75 60 L60 140 M125 60 L140 140" stroke="#EF4444" strokeWidth="6" opacity="0.9" />
      <path d="M30 150 C70 140, 130 140, 170 150" stroke="#38BDF8" strokeWidth="10" strokeLinecap="round" />
      <text x="100" y="178" fill="#38BDF8" fontSize="11" fontWeight="900" textAnchor="middle">MEKONG DAMS</text>
    </svg>
  ),
  "great barrier reef dredging": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#0284C7]">
      <path d="M40 130 L100 40 L160 130 Z" fill="none" stroke="#F43F5E" strokeWidth="8" strokeLinejoin="round" />
      <circle cx="100" cy="110" r="22" fill="#E11D48" />
      <path d="M60 140 H140 L160 170 H40 Z" fill="#1C1917" />
      <text x="100" y="182" fill="#FFFFFF" fontSize="10" fontWeight="900" textAnchor="middle" letterSpacing="0.5">REEF DREDGING</text>
    </svg>
  ),
  "arctic wildlife drilling": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#F8FAFC]">
      <circle cx="100" cy="100" r="80" fill="none" stroke="#000000" strokeWidth="8" />
      <ellipse cx="100" cy="100" rx="40" ry="32" fill="#000000" />
      <polygon points="100,50 145,150 55,150" fill="none" stroke="#E2E8F0" strokeWidth="12" strokeLinejoin="round" />
      <line x1="100" y1="50" x2="100" y2="150" stroke="#EF4444" strokeWidth="8" />
      <text x="100" y="172" fill="#020617" fontSize="11" fontWeight="800" textAnchor="middle">ARCTIC DRILL</text>
    </svg>
  ),
  "sahara solar scammers": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#78350F]">
      <circle cx="100" cy="100" r="80" fill="#EAB308" stroke="#F97316" strokeWidth="6" />
      <polygon points="100,45 135,115 65,115" fill="#1E293B" stroke="#0F172A" strokeWidth="4" />
      <path d="M40 125 H160" stroke="#F97316" strokeWidth="8" strokeLinecap="round" />
      <text x="100" y="165" fill="#EAB308" fontSize="11" fontWeight="900" textAnchor="middle">SAHARA SOLAR</text>
    </svg>
  ),
  "andes mountain mining": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#1C1917]">
      <polygon points="40,140 100,40 160,140" fill="#78716C" />
      <polygon points="80,140 115,80 150,140" fill="#44403C" />
      <path d="M30 140 L170 140" stroke="#F59E0B" strokeWidth="8" strokeLinecap="round" />
      <path d="M100 110 L100 160" stroke="#EF4444" strokeWidth="6" strokeLinecap="round" />
      <text x="100" y="175" fill="#F59E0B" fontSize="11" fontWeight="bold" textAnchor="middle">ANDES MINING</text>
    </svg>
  ),
  "congo cobalt crushers": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#1E3A8A]">
      <polygon points="100,25 165,75 165,140 100,175 35,140 35,75" fill="none" stroke="#60A5FA" strokeWidth="8" />
      <rect x="70" y="75" width="60" height="50" rx="4" fill="#3B82F6" stroke="#93C5FD" strokeWidth="4" />
      <path d="M85 100 H115" stroke="#FFFFFF" strokeWidth="8" strokeLinecap="round" />
      <text x="100" y="162" fill="#60A5FA" fontSize="11" fontWeight="900" textAnchor="middle">COBALT CRUSH</text>
    </svg>
  ),
  "indonesian palm oil king": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#064E3B]">
      <circle cx="100" cy="100" r="75" fill="none" stroke="#EAB308" strokeWidth="6" />
      <path d="M100 45 C100 45, 120 70, 120 90 C120 101, 111 110, 100 110 C89 110, 80 101, 80 90 C80 70, 100 45, 100 45 Z" fill="#EAB308" />
      <path d="M60 130 C75 110, 125 110, 140 130" stroke="#EF4444" strokeWidth="8" strokeLinecap="round" fill="none" />
      <text x="100" y="172" fill="#EAB308" fontSize="11" fontWeight="bold" textAnchor="middle">INDONESIAN PALM</text>
    </svg>
  ),
  "russian gas giants": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#1E1B4B]">
      <path d="M40 130 V50 Q100 20 160 50 V130 Z" fill="none" stroke="#EF4444" strokeWidth="8" strokeLinejoin="round" />
      <circle cx="100" cy="75" r="28" fill="#F43F5E" />
      <path d="M100 40 L100 110 M70 75 H130" stroke="#1E1B4B" strokeWidth="6" />
      <text x="100" y="175" fill="#EF4444" fontSize="11" fontWeight="900" textAnchor="middle" letterSpacing="1">GAS GIANT RU</text>
    </svg>
  ),
  "middle east oil barons": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-1.5 bg-[#451A03]">
      <polygon points="40,130 65,60 100,100 135,60 160,130" fill="#EAB308" stroke="#111827" strokeWidth="6" />
      <circle cx="50" cy="130" r="14" fill="#000000" />
      <circle cx="100" cy="130" r="14" fill="#000000" />
      <circle cx="150" cy="130" r="14" fill="#000000" />
      <text x="100" y="175" fill="#EAB308" fontSize="11" fontWeight="900" textAnchor="middle">OIL BARONS</text>
    </svg>
  ),
  "texas oil tycoons": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#1E293B]">
      <polygon points="100,25 120,70 170,70 130,105 145,155 100,125 55,155 70,105 30,70 80,70" fill="none" stroke="#EF4444" strokeWidth="8" strokeLinejoin="round" />
      <path d="M60 115 L100 50 L140 115" stroke="#FFFFFF" strokeWidth="6" fill="none" />
      <line x1="100" y1="50" x2="100" y2="135" stroke="#FFFFFF" strokeWidth="4" />
      <text x="100" y="172" fill="#EF4444" fontSize="11" fontWeight="900" textAnchor="middle">TEXAS OIL</text>
    </svg>
  ),
  "north sea oil pirates": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#09090B]">
      <circle cx="100" cy="90" r="60" fill="none" stroke="#F59E0B" strokeWidth="8" />
      <path d="M60 115 C60 115 70 65 100 65 C130 65 140 115 140 115 Z" fill="#DC2626" />
      <line x1="45" y1="125" x2="155" y2="125" stroke="#DC2626" strokeWidth="8" strokeLinecap="round" />
      <text x="100" y="175" fill="#F59E0B" fontSize="11" fontWeight="900" textAnchor="middle">SEA PIRATES</text>
    </svg>
  ),
  "global fracking force": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#111111]">
      <polygon points="100,30 165,145 35,145" fill="none" stroke="#DC2626" strokeWidth="8" />
      <path d="M60 130 L100 60 L140 130" stroke="#EA580C" strokeWidth="6" strokeLinecap="round" fill="none" />
      <path d="M30 120 L170 120 M45 100 L155 100" stroke="#EF4444" strokeWidth="4" />
      <text x="100" y="172" fill="#DC2626" fontSize="11" fontWeight="bold" textAnchor="middle">FRACK_FORCE</text>
    </svg>
  ),
  "deep earth drillers": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#2E1065]">
      <circle cx="100" cy="100" r="75" fill="none" stroke="#C084FC" strokeWidth="6" />
      <circle cx="100" cy="100" r="50" fill="none" stroke="#A855F7" strokeWidth="4" />
      <circle cx="100" cy="100" r="25" fill="#EF4444" />
      <polygon points="100,40 108,60 92,60" fill="#C084FC" />
      <polygon points="100,160 108,140 92,140" fill="#C084FC" />
      <text x="100" y="175" fill="#C084FC" fontSize="10" fontWeight="900" textAnchor="middle">DEEP DRILLERS</text>
    </svg>
  ),
  "mountain top removers": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#1C1917]">
      <polygon points="40,140 100,50 160,140" fill="none" stroke="#EF4444" strokeWidth="8" />
      <rect x="70" y="45" width="60" height="40" fill="#1C1917" />
      <line x1="40" y1="140" x2="160" y2="140" stroke="#78716C" strokeWidth="6" />
      <text x="100" y="172" fill="#EF4444" fontSize="11" fontWeight="900" textAnchor="middle">MTN REMOVER</text>
    </svg>
  ),
  "river poisoners inc": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#022C22]">
      <path d="M30 100 C60 60, 140 140, 170 100" stroke="#A3E635" strokeWidth="12" strokeLinecap="round" fill="none" />
      <path d="M30 130 C60 90, 140 170, 170 130" stroke="#84CC16" strokeWidth="12" strokeLinecap="round" fill="none" />
      <circle cx="70" cy="108" r="10" fill="#DC2626" />
      <circle cx="130" cy="122" r="10" fill="#DC2626" />
      <text x="100" y="55" fill="#A3E635" fontSize="11" fontWeight="900" textAnchor="middle">RIVER POISON</text>
    </svg>
  ),
  "air polluters ltd": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-1.5 bg-[#27272A]">
      <rect x="50" y="70" width="30" height="70" fill="#3F3F46" stroke="#18181B" strokeWidth="4" />
      <rect x="120" y="70" width="30" height="70" fill="#3F3F46" stroke="#18181B" strokeWidth="4" />
      <ellipse cx="65" cy="50" rx="20" ry="12" fill="#E4E4E7" opacity="0.8" />
      <ellipse cx="135" cy="50" rx="20" ry="12" fill="#E4E4E7" opacity="0.8" />
      <line x1="50" y1="140" x2="150" y2="140" stroke="#EF4444" strokeWidth="8" strokeLinecap="round" />
      <text x="100" y="172" fill="#EF4444" fontSize="11" fontWeight="bold" textAnchor="middle">AIR POLLUTERS</text>
    </svg>
  ),
  "soil destroyers corp": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#1C1917]">
      <circle cx="100" cy="90" r="60" fill="none" stroke="#B45309" strokeWidth="8" />
      <path d="M50 90 Q100 130 150 90" stroke="#78350F" strokeWidth="6" fill="none" />
      <path d="M55 70 L145 110 M145 70 L55 110" stroke="#EF4444" strokeWidth="6" strokeLinecap="round" />
      <text x="100" y="175" fill="#B45309" fontSize="11" fontWeight="800" textAnchor="middle">SOIL DESTROY</text>
    </svg>
  ),
  "wildlife eradicators": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#09090B]">
      <circle cx="100" cy="100" r="70" fill="none" stroke="#DC2626" strokeWidth="8" />
      <line x1="100" y1="15" x2="100" y2="185" stroke="#DC2626" strokeWidth="4" />
      <line x1="15" y1="100" x2="185" y2="100" stroke="#DC2626" strokeWidth="4" />
      <circle cx="100" cy="100" r="20" fill="none" stroke="#DC2626" strokeWidth="4" />
      <circle cx="100" cy="100" r="5" fill="#DC2626" />
      <text x="100" y="180" fill="#E4E4E7" fontSize="10" fontWeight="950" textAnchor="middle" letterSpacing="0.5">WILDLIFE ERADICATE</text>
    </svg>
  ),
  "ocean acidifiers": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#0C4A6E]">
      <path d="M30 120 C60 100, 140 140, 170 120 L170 160 H30 Z" fill="#0284C7" />
      <circle cx="10" cy="10" r="8" fill="#ADF3B3" transform="translate(90 60)" />
      <circle cx="10" cy="10" r="12" fill="#ADF3B3" transform="translate(70 85)" />
      <circle cx="10" cy="10" r="12" fill="#ADF3B3" transform="translate(110 85)" />
      <text x="100" y="180" fill="#38BDF8" fontSize="11" fontWeight="900" textAnchor="middle">OCEAN ACIDIFY</text>
    </svg>
  ),
  "glacier melters": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#0C1E40]">
      <polygon points="100,30 160,130 40,130" fill="#0EA5E9" />
      <polygon points="100,60 150,130 50,130" fill="#E2E8F0" />
      <path d="M100 130 C100 130 115 155 115 170 C115 178 108 185 100 185 C92 185 85 178 85 170 C85 155 100 130 100 130 Z" fill="#38BDF8" />
      <text x="100" y="110" fill="#52525B" fontSize="10" fontWeight="bold" textAnchor="middle">MELTING</text>
    </svg>
  ),
  "ozone depleters": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-1.5 bg-[#1E1B4B]">
      <circle cx="100" cy="100" r="70" fill="none" stroke="#F43F5E" strokeWidth="8" />
      <path d="M100 22 C115 22, 120 40, 120 40 L100 85" stroke="#F43F5E" strokeWidth="6" strokeLinecap="round" fill="none" />
      <circle cx="100" cy="100" r="25" fill="#3B82F6" />
      <text x="100" y="175" fill="#EF4444" fontSize="11" fontWeight="bold" textAnchor="middle">OZONE DEPLETE</text>
    </svg>
  ),
  "coral reef crushers": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#0F172A]">
      <path d="M40 140 H160" stroke="#475569" strokeWidth="8" strokeLinecap="round" />
      <path d="M70 140 Q100 80 130 140" stroke="#F43F5E" strokeWidth="12" fill="none" />
      <line x1="100" y1="140" x2="100" y2="50" stroke="#EF4444" strokeWidth="8" strokeLinecap="round" />
      <text x="100" y="172" fill="#F43F5E" fontSize="11" fontWeight="bold" textAnchor="middle">CORAL REEF CO</text>
    </svg>
  ),
  "mangrove destroyers": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-1.5 bg-[#14532D]">
      <circle cx="100" cy="90" r="60" fill="none" stroke="#78350F" strokeWidth="8" />
      <line x1="100" y1="30" x2="100" y2="150" stroke="#78350F" strokeWidth="6" />
      <line x1="40" y1="120" x2="160" y2="120" stroke="#EF4444" strokeWidth="10" strokeLinecap="round" />
      <text x="100" y="175" fill="#F87171" fontSize="11" fontWeight="900" textAnchor="middle">MANGROVE CO</text>
    </svg>
  ),
  "wetland drainers": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#172554]">
      <rect x="50" y="50" width="100" height="100" fill="none" stroke="#3B82F6" strokeWidth="8" />
      <line x1="50" y1="100" x2="150" y2="100" stroke="#1E3A8A" strokeWidth="4" />
      <line x1="100" y1="50" x2="100" y2="150" stroke="#1E3A8A" strokeWidth="4" />
      <line x1="40" y1="120" x2="160" y2="120" stroke="#EF4444" strokeWidth="10" strokeLinecap="round" />
      <text x="100" y="172" fill="#E0F2FE" fontSize="11" fontWeight="bold" textAnchor="middle">WETLAND DRAIN</text>
    </svg>
  ),
  "forest burners": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-1.5 bg-[#450A0A]">
      <circle cx="100" cy="100" r="75" fill="none" stroke="#EF4444" strokeWidth="6" />
      <polygon points="100,45 135,135 65,135" fill="#B91C1C" />
      <path d="M100 45 C100 45 125 90 125 115 C125 129 114 140 100 140 C86 140 75 129 75 115 C75 90 100 45 100 45 Z" fill="#F59E0B" />
      <text x="100" y="175" fill="#EF4444" fontSize="11" fontWeight="950" textAnchor="middle">FOREST BURNERS</text>
    </svg>
  ),
  "species extinguishers": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#1C1917]">
      <circle cx="100" cy="90" r="55" fill="none" stroke="#78716C" strokeWidth="6" />
      <path d="M50 140 H150" stroke="#EF4444" strokeWidth="8" strokeLinecap="round" />
      <line x1="75" y1="80" x2="125" y2="130" stroke="#EF4444" strokeWidth="8" strokeLinecap="round" />
      <line x1="125" y1="80" x2="75" y2="130" stroke="#EF4444" strokeWidth="8" strokeLinecap="round" />
      <text x="100" y="175" fill="#E7E5E4" fontSize="10" fontWeight="900" textAnchor="middle">SPECIES EXTINCT</text>
    </svg>
  ),
  "planet eaters": (
    <svg viewBox="0 0 200 200" className="w-full h-full p-2 bg-[#020617]">
      <circle cx="100" cy="100" r="75" fill="none" stroke="#8B5CF6" strokeWidth="8" />
      <circle cx="100" cy="100" r="45" fill="#3B82F6" />
      <path d="M30 100 Q100 40 170 100" stroke="#A78BFA" strokeWidth="6" strokeLinecap="round" fill="none" />
      <circle cx="90" cy="70" r="10" fill="#EF4444" />
      <circle cx="130" cy="95" r="10" fill="#EF4444" />
      <circle cx="70" cy="115" r="10" fill="#EF4444" />
      <text x="100" y="185" fill="#8B5CF6" fontSize="11" fontWeight="900" textAnchor="middle" letterSpacing="1">PLANET EATERS</text>
    </svg>
  )
};

export function CompanyLogo({ companyName, logoUrl, domain, className, size = "md", sector: passedSector }: CompanyLogoProps) {
  const [fallbackStep, setFallbackStep] = React.useState(0);
  const [loaded, setLoaded] = React.useState(false);

  // Reset fallback steps and loaded status when the target company name, logoUrl, or domain changes to ensure accurate rendering and prevent state leakage
  React.useEffect(() => {
    setFallbackStep(0);
    setLoaded(false);
  }, [companyName, logoUrl, domain]);

  const getInitials = (name: string) => {
    return name.charAt(0).toUpperCase();
  };

  const getBgColor = (name: string) => {
    const colors = [
      "bg-slate-900",
      "bg-zinc-900",
      "bg-stone-900",
      "bg-neutral-900",
      "bg-red-900",
      "bg-orange-900",
      "bg-amber-900",
      "bg-yellow-900",
      "bg-lime-900",
      "bg-green-900",
      "bg-emerald-900",
      "bg-teal-900",
      "bg-cyan-900",
      "bg-sky-900",
      "bg-blue-900",
      "bg-indigo-900",
      "bg-violet-900",
      "bg-purple-900",
      "bg-fuchsia-900",
      "bg-pink-900",
      "bg-rose-900",
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const sizeClasses: Record<string, string> = {
    xs: "w-5 h-5 text-[8px]",
    sm: "w-6 h-6 text-[10px]",
    md: "w-10 h-10 text-sm",
    lg: "w-12 h-12 text-xl",
    xl: "w-20 h-20 text-3xl",
  };

  const iconSizeClasses: Record<string, string> = {
    xs: "w-2.5 h-2.5",
    sm: "w-3 h-3",
    md: "w-5 h-5",
    lg: "w-6 h-6",
    xl: "w-10 h-10",
  };

  const normName = companyName.toLowerCase().trim();
  const activeDomain = domain ? domain.toLowerCase().trim() : (companyName.toLowerCase().replace(/[^a-z0-9]/g, "") + ".com");

  // Lookup matching company from static registry to grab the sector
  const matchedCompany = null;

  const sector = React.useMemo(() => {
    if (passedSector && passedSector !== "Other" && passedSector !== "all") {
      return passedSector;
    }
    
    if (matchedCompany?.sector) {
      return matchedCompany.sector;
    }

    // Keyword lookups as robust fallback
    const nameStr = normName;
    const domStr = activeDomain;

    if (
      nameStr.includes("conoco") || 
      nameStr.includes("philips") || 
      nameStr.includes("phillips") || 
      nameStr.includes("chevron") || 
      nameStr.includes("exxon") || 
      nameStr.includes("shell") || 
      nameStr.includes("bp") || 
      nameStr.includes("gazprom") || 
      nameStr.includes("aramco") || 
      nameStr.includes("rosneft") || 
      nameStr.includes("lukoil") || 
      nameStr.includes("petro") || 
      nameStr.includes("oil") || 
      nameStr.includes("gas") || 
      nameStr.includes("energy") ||
      domStr.includes("conoco") ||
      domStr.includes("oil") ||
      domStr.includes("gas")
    ) {
      return "Oil & Gas";
    }

    if (
      nameStr.includes("fortescue") || 
      nameStr.includes("bhp") || 
      nameStr.includes("rio tinto") || 
      nameStr.includes("vale") || 
      nameStr.includes("mining") || 
      nameStr.includes("metal") || 
      nameStr.includes("steel") || 
      nameStr.includes("mine") || 
      nameStr.includes("mineral") || 
      nameStr.includes("aluminum") || 
      nameStr.includes("chalco") ||
      nameStr.includes("vedanta") ||
      domStr.includes("fortescue") ||
      domStr.includes("mining") ||
      domStr.includes("metal")
    ) {
      return "Mining";
    }

    if (
      nameStr.includes("dow") || 
      nameStr.includes("dupont") || 
      nameStr.includes("chemical") || 
      nameStr.includes("chem") || 
      nameStr.includes("plastics") || 
      nameStr.includes("basf") || 
      nameStr.includes("bayer") || 
      domStr.includes("dow") || 
      domStr.includes("chem")
    ) {
      return "Chemicals";
    }

    if (
      nameStr.includes("indofood") || 
      nameStr.includes("food") || 
      nameStr.includes("agri") || 
      nameStr.includes("forest") || 
      nameStr.includes("wood") || 
      nameStr.includes("palm") || 
      nameStr.includes("jbs") || 
      nameStr.includes("wilmar") || 
      nameStr.includes("cargill") || 
      nameStr.includes("nestle") || 
      nameStr.includes("crop") ||
      nameStr.includes("fmcg") ||
      nameStr.includes("consumer") ||
      nameStr.includes("retail") ||
      nameStr.includes("goods") ||
      domStr.includes("indofood") ||
      domStr.includes("food") ||
      domStr.includes("agri")
    ) {
      return "Agriculture";
    }

    return "Other";
  }, [passedSector, matchedCompany, normName, activeDomain]);

  const getSectorIconAndBg = (sectorName: string) => {
    const name = sectorName.toLowerCase().trim();

    // 1. Oil & Gas
    if (name.includes("oil") || name.includes("gas") || name.includes("petrol") || name.includes("energy")) {
      return { 
        icon: Flame, 
        bg: "bg-red-950 border border-red-800 text-red-100",
        accent: "text-red-400"
      };
    }

    // 2. Mining
    if (name.includes("mining") || name.includes("mine") || name.includes("coal") || name.includes("steel") || name.includes("metal")) {
      return { 
        icon: Pickaxe, 
        bg: "bg-amber-950 border border-amber-800 text-amber-200",
        accent: "text-amber-400"
      };
    }

    // 3. Utilities
    if (name.includes("utility") || name.includes("utilities") || name.includes("power") || name.includes("water")) {
      return { 
        icon: Zap, 
        bg: "bg-yellow-950 border border-yellow-800 text-yellow-100",
        accent: "text-yellow-400"
      };
    }

    // 4. Transport
    if (name.includes("transport") || name.includes("shipping") || name.includes("aviation") || name.includes("airline") || name.includes("logistics")) {
      return { 
        icon: Truck, 
        bg: "bg-zinc-900 border border-zinc-700 text-zinc-100",
        accent: "text-zinc-400"
      };
    }

    // 5. Automotive
    if (name.includes("automotive") || name.includes("car") || name.includes("motor") || name.includes("vehicle")) {
      return { 
        icon: Car, 
        bg: "bg-neutral-900 border border-neutral-700 text-neutral-100",
        accent: "text-neutral-400"
      };
    }

    // 6. Chemicals
    if (name.includes("chemical") || name.includes("chem") || name.includes("fertilizer") || name.includes("plastic")) {
      return { 
        icon: FlaskConical, 
        bg: "bg-fuchsia-950 border border-fuchsia-800 text-fuchsia-100",
        accent: "text-fuchsia-400"
      };
    }

    // 7. Agriculture
    if (name.includes("agri") || name.includes("agriculture") || name.includes("crop") || name.includes("sprout") || name.includes("wood") || name.includes("palm") || name.includes("forest") || name.includes("timber") || name.includes("bean")) {
      return { 
        icon: Sprout, 
        bg: "bg-emerald-950 border border-emerald-800 text-emerald-100",
        accent: "text-emerald-400"
      };
    }

    // 8. Manufacturing
    if (name.includes("manufacturing") || name.includes("manufactur") || name.includes("factory") || name.includes("cement") || name.includes("glass") || name.includes("heavy industry")) {
      return { 
        icon: Factory, 
        bg: "bg-slate-900 border border-slate-700 text-slate-100",
        accent: "text-slate-400"
      };
    }

    // 9. Technology
    if (name.includes("tech") || name.includes("technology") || name.includes("soft") || name.includes("digital") || name.includes("telecom") || name.includes("semiconductor") || name.includes("hardware")) {
      return { 
        icon: Cpu, 
        bg: "bg-cyan-950 border border-cyan-800 text-cyan-100",
        accent: "text-cyan-400"
      };
    }

    // 10. Finance
    if (name.includes("finance") || name.includes("bank") || name.includes("insur") || name.includes("investment") || name.includes("asset")) {
      return { 
        icon: Landmark, 
        bg: "bg-blue-950 border border-blue-800 text-blue-100",
        accent: "text-blue-400"
      };
    }

    // 11. Consumer Goods & Food
    if (name.includes("consumer") || name.includes("goods") || name.includes("fmcg") || name.includes("food") || name.includes("beverage") || name.includes("feed") || name.includes("dairy")) {
      return { 
        icon: Package, 
        bg: "bg-stone-900 border border-stone-700 text-stone-100",
        accent: "text-stone-400"
      };
    }

    // 12. Retail
    if (name.includes("retail") || name.includes("shop") || name.includes("store") || name.includes("market") || name.includes("supermarket") || name.includes("e-commerce")) {
      return { 
        icon: ShoppingBag, 
        bg: "bg-rose-950 border border-rose-800 text-rose-100",
        accent: "text-rose-400"
      };
    }

    // 13. Pharmaceuticals
    if (name.includes("pharma") || name.includes("pharmaceutical") || name.includes("health") || name.includes("bio") || name.includes("vaccine") || name.includes("drug")) {
      return { 
        icon: Pill, 
        bg: "bg-purple-950 border border-purple-800 text-purple-100",
        accent: "text-purple-400"
      };
    }

    // 14. Building & Other Fallbacks
    if (name.includes("construct") || name.includes("estate") || name.includes("build")) {
      return { 
        icon: Building, 
        bg: "bg-stone-800 border border-stone-700 text-stone-200",
        accent: "text-stone-400"
      };
    }

    return { 
      icon: Building, 
      bg: "bg-neutral-900 border border-neutral-800 text-neutral-300",
      accent: "text-neutral-400"
    };
  };

  const sectorMetrics = getSectorIconAndBg(sector);
  const SectorIcon = sectorMetrics.icon;

  // Check custom inline high-fidelity SVGs first to bypass network issues/sanctions/hotlink bans entirely
  let inlineLogo = CUSTOM_INLINE_LOGOS[normName] || CUSTOM_INLINE_LOGOS[activeDomain];
  
  if (!inlineLogo) {
    const cleanedCompany = normName.replace(/[^a-z0-9]/g, "");
    for (const [key, val] of Object.entries(CUSTOM_INLINE_LOGOS)) {
      const cleanedKey = key.toLowerCase().replace(/[^a-z0-9]/g, "");
      if (cleanedKey.length >= 3) {
        if (cleanedCompany.includes(cleanedKey) || cleanedKey.includes(cleanedCompany) || normName.includes(key) || key.includes(normName)) {
          inlineLogo = val;
          break;
        }
      }
    }
  }

  // Generate a distinct, stunning geometric brand emblem for fallback logo representation
  const renderFallbackEmblem = () => {
    let hash = 0;
    for (let i = 0; i < companyName.length; i++) {
      hash = companyName.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = Math.abs(hash % 360);
    // Unique gradient style to avoid conflicts in definitions
    const bgGrad = `linear-gradient(135deg, hsl(${hue}, 82%, 36%) 0%, hsl(${(hue + 45) % 360}, 85%, 16%) 100%)`;
    
    return (
      <div 
        style={{ background: bgGrad }}
        className="w-full h-full flex items-center justify-center relative p-1 pb-1.5 border border-white/10 rounded-sm shadow-inner overflow-hidden"
      >
        {/* Ambient background glow pattern */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_transparent_30%,_rgba(0,0,0,0.45)_105%)] pointer-events-none" />
        
        {/* Subtle geometric framing */}
        <div className="absolute inset-1 border border-white/10 rounded-xs pointer-events-none" />
        
        {/* Beautiful high-fidelity sector icon with elegant drop shadow */}
        <SectorIcon 
          className={cn(
            "text-white stroke-[2.5] drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)] transition-transform duration-300 group-hover:scale-110",
            size === "xs" ? "w-3 h-3" :
            size === "sm" ? "w-3.5 h-3.5" :
            size === "md" ? "w-5 h-5" :
            size === "lg" ? "w-7 h-7" : "w-11 h-11"
          )}
        />

        {/* Decorative corner dots for a pristine precision look */}
        <div className="absolute top-1 left-1 w-0.5 h-0.5 rounded-full bg-white/30" />
        <div className="absolute top-1 right-1 w-0.5 h-0.5 rounded-full bg-white/30" />
        <div className="absolute bottom-1 left-1 w-0.5 h-0.5 rounded-full bg-white/30" />
        <div className="absolute bottom-1 right-1 w-0.5 h-0.5 rounded-full bg-white/30" />
      </div>
    );
  };

  const cleanDom = React.useMemo(() => {
    if (!activeDomain) return "";
    return activeDomain.replace(/\s+/g, "").toLowerCase();
  }, [activeDomain]);

  const currentLogoUrl = React.useMemo(() => {
    // Step 0: Try the high-quality Clearbit CDN or direct logoUrl
    if (fallbackStep === 0) {
      if (CUSTOM_COMPANY_LOGOS[normName]) return CUSTOM_COMPANY_LOGOS[normName];
      if (CUSTOM_COMPANY_LOGOS[activeDomain]) return CUSTOM_COMPANY_LOGOS[activeDomain];

      let targetUrl: string | null = null;
      if (logoUrl) {
        targetUrl = logoUrl;
      } else if (cleanDom && cleanDom !== "other.com" && cleanDom.includes(".")) {
        targetUrl = `https://logo.clearbit.com/${cleanDom}`;
      }

      if (targetUrl) {
        const lowerTargetUrl = targetUrl.toLowerCase().trim();
        if (CUSTOM_COMPANY_LOGOS[lowerTargetUrl]) return CUSTOM_COMPANY_LOGOS[lowerTargetUrl];
        for (const [key, val] of Object.entries(CUSTOM_COMPANY_LOGOS)) {
          if (key.startsWith("https://") && lowerTargetUrl.includes(key)) {
            return val;
          }
        }
        return targetUrl;
      }
    }

    // Step 1: Fallback to high-resolution Google Favicon API (supports sz=128)
    if (fallbackStep === 1) {
      if (cleanDom && cleanDom !== "other.com" && cleanDom.includes(".")) {
        return `https://www.google.com/s2/favicons?sz=128&domain=${cleanDom}`;
      }
    }

    // Step 2: Fallback to DuckDuckGo Favicon API
    if (fallbackStep === 2) {
      if (cleanDom && cleanDom !== "other.com" && cleanDom.includes(".")) {
        return `https://icons.duckduckgo.com/ip3/${cleanDom}.ico`;
      }
    }

    return null;
  }, [fallbackStep, logoUrl, cleanDom, normName, activeDomain]);

  const handleImageError = () => {
    setLoaded(false);
    setFallbackStep(prev => prev + 1);
  };

  if (inlineLogo) {
    const isEvercove = normName === "evercove" || activeDomain === "evercove.com";
    const isCircular = isEvercove || normName.includes("pellegrino") || normName.includes("cartier") || className?.includes("rounded-full");
    return (
      <div className={cn("shrink-0 overflow-hidden bg-white flex items-center justify-center relative", isCircular ? "rounded-full" : "rounded-sm", sizeClasses[size], className)}>
        {inlineLogo}
      </div>
    );
  }

  if (currentLogoUrl && fallbackStep <= 2) {
    const pixelSizes: Record<string, number> = {
      xs: 20,
      sm: 24,
      md: 40,
      lg: 48,
      xl: 80,
    };
    const numSize = pixelSizes[size] || 40;
    const isCircular = className?.includes("rounded-full");

    return (
      <div className={cn("shrink-0 overflow-hidden bg-white flex items-center justify-center relative", isCircular ? "rounded-full" : "rounded-sm", sizeClasses[size], className)}>
        {!loaded && (
          <div className="absolute inset-0 bg-stone-100 animate-pulse" />
        )}
        <motion.img
          key={`${currentLogoUrl}-${fallbackStep}`}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: loaded ? 1 : 0, scale: loaded ? 1 : 0.8 }}
          transition={{ duration: 0.2 }}
          src={currentLogoUrl}
          alt={`${companyName} official logo`}
          width={numSize}
          height={numSize}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-contain p-1 relative z-10"
          onLoad={() => setLoaded(true)}
          onError={handleImageError}
          referrerPolicy="no-referrer"
        />
      </div>
    );
  }

  const isCircularFallback = className?.includes("rounded-full");
  return (
    <div 
      className={cn(
        "shrink-0 select-none relative overflow-hidden flex items-center justify-center bg-white",
        isCircularFallback ? "rounded-full" : "rounded-sm",
        sizeClasses[size],
        className
      )}
    >
      {renderFallbackEmblem()}
    </div>
  );
}
